import type { Locale } from "@confluence/shared";

// Anglais : langue par défaut et référence. Les autres langues doivent avoir toutes les clés.
const en = {
  "lang.name": "English",
  "common.language": "Language",
  "common.email": "Email",
  "common.password": "Password",
  "common.name": "Name",
  "common.save": "Save",
  "common.saved": "Saved",
  "common.cancel": "Cancel",
  "common.continue": "Continue",
  "common.back": "Back",
  "common.loading": "Loading…",
  "common.passwordHint": "At least 10 characters.",
  "common.backToLogin": "Back to sign in",

  "login.title": "Sign in",
  "login.remember": "Keep me signed in",
  "login.submit": "Sign in",
  "login.forgot": "Forgot your password?",
  "login.noAccount": "No account yet?",
  "login.create": "Create an account",

  "signup.title": "Create your account",
  "signup.invited": "You were invited with {email}.",
  "signup.submit": "Create my account",
  "signup.haveAccount": "Already have an account?",
  "signup.closed": "Sign-ups are closed on this instance.",
  "signup.inviteOnly": "This instance is invitation-only. Ask its administrator for an invitation link.",
  "signup.checkTitle": "Check your inbox",
  "signup.checkBody": "We sent a confirmation link to {email}. It's valid for 24 hours.",
  "signup.resend": "Send the link again",
  "signup.resent": "If the address is waiting for confirmation, a new link is on its way.",

  "verify.title": "Confirming your address",
  "verify.working": "One moment…",

  "forgot.title": "Reset your password",
  "forgot.body": "Enter your email. If an account exists, you'll receive a link valid for 1 hour.",
  "forgot.submit": "Send the link",
  "forgot.sent": "If an account exists for this address, a link is on its way.",

  "reset.title": "Choose a new password",
  "reset.submit": "Save the password",

  "setup.title": "Set up Maki Calendar",
  "setup.intro": "Three steps: your administrator account, email sending, and who can sign up.",
  "setup.step.admin": "Administrator",
  "setup.step.mail": "Emails",
  "setup.step.access": "Sign-ups",
  "setup.brevoKey": "Brevo API key",
  "setup.brevoHelp": "In Brevo, open SMTP & API, then API keys. Also authenticate your sender domain (SPF and DKIM) so emails don't land in spam.",
  "setup.fromEmail": "Sender address",
  "setup.fromName": "Sender name",
  "setup.timezone": "Default time zone",
  "setup.finish": "Finish setup",
  "setup.noToken": "This setup link is incomplete. Open the full link printed by the install script.",
  "setup.done": "Setup is complete.",

  "mode.label": "Who can create an account?",
  "mode.invite": "Invited people only",
  "mode.invite.help": "You invite people by email. Recommended.",
  "mode.open": "Anyone",
  "mode.open.help": "Anyone who finds the address can sign up.",
  "mode.closed": "Nobody",
  "mode.closed.help": "No new accounts, not even with an invitation.",

  "nav.calendar": "Calendar",
  "nav.settings": "Settings",
  "nav.logout": "Sign out",

  "home.hello": "Hello, {name}",
  "home.emptyTitle": "Connect your first calendar",
  "home.emptyBody": "Your account is ready. Connecting Google, Microsoft 365, CalDAV and iCal calendars comes in the next version.",
  "home.soon": "Next version",

  "settings.title": "Settings",
  "settings.account": "My account",
  "settings.security": "Security",
  "settings.instance": "Instance",
  "settings.users": "Users",
  "settings.adminOnly": "Administrator",

  "profile.timezone": "Time zone",
  "profile.weekStart": "First day of the week",
  "profile.home": "Default city",
  "profile.homePlaceholder": "Brussels, for example",
  "profile.homeHint": "Place suggestions are sorted around your current position, or around this city when it's unknown.",
  "day.1": "Monday",
  "day.0": "Sunday",
  "day.6": "Saturday",

  "password.current": "Current password",
  "password.new": "New password",
  "password.change": "Change password",
  "password.changed": "Password changed. Your other sessions were signed out.",

  "instance.mail": "Emails (Brevo)",
  "instance.keySaved": "A key is saved. Leave empty to keep it.",
  "instance.test": "Send a test email",
  "instance.testSent": "Test email sent to {email}.",

  "users.invite": "Invite someone",
  "users.inviteSubmit": "Send the invitation",
  "users.invited": "Invitation sent to {email}.",
  "users.disable": "Disable",
  "users.enable": "Enable",
  "users.delete": "Delete",
  "users.confirmDelete": "Delete {name}'s account? This can't be undone.",
  "users.you": "you",
  "users.admin": "administrator",
  "users.unverified": "email not confirmed",
  "users.disabled": "disabled",

  "error.INVALID_CREDENTIALS": "Wrong email or password.",
  "error.EMAIL_NOT_VERIFIED": "Confirm your email address first, using the link we sent you.",
  "error.ACCOUNT_DISABLED": "This account was disabled by the administrator.",
  "error.EMAIL_TAKEN": "An account already exists with this address.",
  "error.REGISTRATION_CLOSED": "Sign-ups are closed on this instance.",
  "error.INVITATION_REQUIRED": "Use the email address that received the invitation.",
  "error.INVALID_TOKEN": "This link is no longer valid. Ask for a new one.",
  "error.SETUP_DONE": "Setup is already complete. Sign in instead.",
  "error.SETUP_TOKEN_INVALID": "The setup link is wrong. Use the one printed by the install script.",
  "error.SETUP_TOKEN_MISSING": "SETUP_TOKEN is missing from the server's .env file.",
  "error.MAIL_NOT_CONFIGURED": "Email sending isn't configured yet. The administrator can set it up in Settings.",
  "error.MAIL_FAILED": "The email could not be sent. Check the Brevo key and sender address.",
  "error.RATE_LIMITED": "Too many attempts. Wait a minute and try again.",
  "error.VALIDATION": "Some fields need fixing.",
  "error.NETWORK": "Can't reach the server. Check your connection.",
  "error.UNAUTHENTICATED": "Your session has ended. Sign in again.",
  "error.FORBIDDEN": "Only the administrator can do this.",
  "error.CSRF": "The request was refused. Reload the page and try again.",
  "error.INTERNAL": "Something went wrong on the server. Try again in a moment.",
  "error.field": "Check this field.",
  "settings.calendars": "Calendars",
  "cal.lead": "Add as many accounts as you like. For each calendar, choose its color, whether it's shown, whether it blocks your booking page, and what people you share with see by default.",
  "cal.add": "Add an account",
  "cal.google.sub": "Gmail or Google Workspace",
  "cal.microsoft.sub": "Outlook, work or personal",
  "cal.caldav.sub": "Nextcloud, iCloud, Fastmail…",
  "cal.ics": "iCal link",
  "cal.ics.sub": "Read-only subscription",
  "cal.notConfigured": "The administrator must first set up the {provider} app in Settings, Instance.",
  "cal.continueWith": "Continue with {provider}",
  "cal.redirectNote": "You'll be sent to {provider} to allow Maki Calendar to read and edit your calendar. No access to your emails is requested.",
  "cal.service": "Service",
  "cal.serverUrl": "Server address",
  "cal.username": "Username",
  "cal.appPassword": "App password",
  "cal.appPasswordHint": "Use an app password rather than your main password. It's required for iCloud.",
  "cal.connect": "Connect and find calendars",
  "cal.icsUrl": "Calendar link",
  "cal.icsName": "Display name",
  "cal.subscribe": "Subscribe",
  "cal.other": "Other server",
  "cal.status.ok": "Synced {when}",
  "cal.status.never": "Waiting for the first sync",
  "cal.status.syncing": "Syncing…",
  "cal.status.error": "Sync problem: {error}",
  "cal.status.reauth": "Access was refused. Reconnect the account.",
  "cal.sync": "Sync now",
  "cal.reconnect": "Reconnect",
  "cal.remove": "Remove",
  "cal.removeConfirm": "Remove {label}? Its calendars disappear from Maki Calendar. Nothing is deleted at the provider.",
  "cal.removeOk": "Remove the account",
  "cal.keep": "Keep",
  "cal.show": "Show",
  "cal.blocks": "Blocks booking",
  "cal.shareDefault": "Shared as",
  "cal.default": "Default",
  "cal.readOnly": "read-only",
  "cal.color": "Color of {name}",
  "cal.connected": "{provider} account connected. Its calendars are syncing.",
  "cal.added": "Account added. Its calendars are syncing.",
  "cal.empty": "No account connected yet.",
  "vis.hidden": "Hidden",
  "vis.busy": "Busy",
  "vis.details": "Details",
  "oauth.not_configured": "This connection isn't set up yet. The administrator must configure the app in Settings, Instance.",
  "oauth.cancelled": "Connection cancelled.",
  "oauth.expired": "The connection took too long. Try again.",
  "oauth.failed": "The connection failed. Try again.",
  "oauth.admin_consent": "Your organization requires an administrator to approve Maki Calendar. Send this link to your Microsoft 365 administrator:",
  "instance.providers": "Calendar apps (OAuth)",
  "instance.providersHelp": "Create the apps at Google and Microsoft, then paste their identifiers here. Each app needs the redirect URI shown below.",
  "instance.clientId": "Client ID",
  "instance.clientSecret": "Client secret",
  "instance.secretSaved": "A secret is saved. Leave empty to keep it.",
  "instance.redirectUri": "Redirect URI",
  "home.upcoming": "Next 14 days",
  "home.nothing": "Nothing planned.",
  "home.manage": "Manage calendars",
  "home.addFirst": "Add a calendar",
  "home.attention": "An account needs your attention.",
  "home.connectBody": "Connect Google, Microsoft 365, CalDAV (Nextcloud, iCloud…) or an iCal link to see everything in one place.",
  "common.allDay": "All day",
  "common.join": "Join",
  "common.today": "Today",
  "common.tomorrow": "Tomorrow",
  "view.week": "Week",
  "view.month": "Month",
  "view.agenda": "Agenda",
  "view.boxes": "Boxes",
  "view.label": "View",
  "cal.prev": "Previous",
  "cal.next": "Next",
  "cal.weekNumber": "Week {n}",
  "cal.calendars": "Calendars",
  "cal.toggleSidebar": "Calendars and mini calendar",
  "cal.more": "+{n} more",
  "cal.earlier": "Show earlier days",
  "cal.later": "Show later days",
  "cal.windowEnd": "Calendars are synced up to 18 months ahead.",
  "cal.windowStart": "Calendars are synced from 6 months back.",
  "cal.noTitle": "(No title)",
  "cal.shortcuts": "Shortcuts: N new event, T today, J/K or ← → previous and next, D 3 W M L B to change view, / search.",
  "ev.free": "Shown as free",
  "ev.tentative": "Tentative",
  "ev.private": "Private",
  "ev.recurring": "Repeats",
  "ev.declined": "Declined",
  "ev.guests": "Guests",
  "ev.organizer": "Organizer",
  "ev.status.accepted": "Accepted",
  "ev.status.declined": "Declined",
  "ev.status.tentative": "Maybe",
  "ev.status.needs-action": "Awaiting reply",
  "ev.openMap": "Open in a map",
  "ev.close": "Close",
  "ev.notes": "Notes",
  "ev.location": "Place",
  "cal.calendar": "Calendar",
  "guide.google": "How to create the Google app",
  "guide.google.1": "Open console.cloud.google.com and create a project (for example “Maki Calendar”).",
  "guide.google.2": "In APIs & Services, Library, enable “Google Calendar API”.",
  "guide.google.3": "Open Google Auth Platform (OAuth consent screen), Get started: app name, support email, audience “External”, contact email.",
  "guide.google.4": "In Branding: home page {home}, privacy policy {privacy}, terms of service {terms}, authorized domain {domain}.",
  "guide.google.5": "In Data access, add the scopes openid, email, profile and https://www.googleapis.com/auth/calendar.",
  "guide.google.6": "In Clients, Create client, type “Web application”, authorized redirect URI: the address shown above.",
  "guide.google.7": "Paste the client ID and secret here, then Save.",
  "guide.google.8": "In Audience, Publish the app. To remove the “unverified app” warning, ask for verification in the Verification centre: it uses the pages above and a short demo video.",
  "guide.microsoft": "How to create the Microsoft app",
  "guide.microsoft.1": "Sign in to entra.microsoft.com with an account that has its own directory (a free Azure account creates one).",
  "guide.microsoft.2": "App registrations, New registration. Supported accounts: “Accounts in any organizational directory and personal Microsoft accounts”. Redirect URI, type Web: the address shown above.",
  "guide.microsoft.3": "Copy the Application (client) ID.",
  "guide.microsoft.4": "Certificates & secrets, New client secret (24 months). Copy the Value, not the Secret ID, and note the expiry date.",
  "guide.microsoft.5": "API permissions, Add, Microsoft Graph, Delegated: openid, profile, email, offline_access, User.Read, Calendars.ReadWrite. Don't grant admin consent.",
  "guide.microsoft.6": "Paste the ID and the secret here, then Save.",
  "guide.microsoft.7": "Optional: to avoid “administrator approval required” in some organizations, become a verified publisher (Branding & properties) with a Microsoft Cloud Partner Program ID and a verified domain.",
  "legal.privacy": "Privacy policy",
  "legal.terms": "Terms of service",
  "instance.legal": "Legal pages",
  "instance.legalHelp": "Google asks for these pages to verify your app. They're generated from what Maki Calendar actually does; you can replace them.",
  "instance.legalOperator": "Operator (person or company)",
  "instance.legalContact": "Contact email",
  "instance.legalEdit": "Edit the text",
  "instance.legalGenerated": "Generated text in use.",
  "instance.legalReset": "Go back to the generated text",
  "settings.status": "Status",
  "status.version": "Version",
  "status.since": "running since {when}",
  "status.database": "Database",
  "status.mail": "Email sending",
  "status.push": "Instant sync",
  "status.pushOff": "Off: the address must be public HTTPS.",
  "status.users": "Users",
  "status.connections": "Connected accounts",
  "status.failing": "Accounts with a problem",
  "status.noFailing": "No account has a problem.",
  "status.backups": "Backups",
  "status.noBackups": "No backup listed yet. They run every night at 3:15.",
  "status.restore": "Restore: sudo ./scripts/restore.sh /var/backups/confluence/<file>",
  "status.errors": "Recent errors",
  "status.noErrors": "No recent error.",
  "status.ok": "OK",
  "status.problem": "Problem",
  "bk.busyCalendars": "These calendars block your booking page: {list}. Change “Counts as busy” in Calendars.",
  "bk.busyNone": "No calendar counts as busy: every time in your hours is offered. Turn on “Counts as busy” in Calendars.",
  "ev.scope.following": "This event and the following ones",
  "ev.repeat.custom": "This series uses a rule the form can't show (for example “the 2nd Tuesday of the month”). Change it in Google, Microsoft or your CalDAV app.",
  "instance.places": "Place search",
  "instance.placesHelp": "By default, places come from OpenStreetMap (free, no key). With a Google Places API key, suggestions come from Google, which knows more shops and restaurants. Google bills the searches beyond its free monthly allowance.",
  "instance.placesKey": "Google Places API key",
  "instance.placesSaved": "A key is saved. Leave empty to keep it.",
  "instance.placesNone": "No key: OpenStreetMap is used.",
  "instance.placesRemove": "Remove the key and go back to OpenStreetMap",
  "settings.notifications": "Notifications",
  "notify.intro": "Choose the emails Maki Calendar sends you. Visitors always receive their booking confirmations.",
  "notify.bookings": "Bookings",
  "notify.bookings.hint": "An email for each new booking, cancellation or change on your booking page.",
  "notify.shares": "New shares",
  "notify.shares.hint": "An email when someone shares their calendar with you.",
  "notify.reconnect": "Accounts to reconnect",
  "notify.reconnect.hint": "An email when an account hasn't synced for more than 24 hours.",
  "notify.dailyRecap": "Tomorrow's summary",
  "notify.dailyRecap.hint": "Every evening at 6 pm, the list of your events for the next day.",
  "view.day": "Day",
  "view.threeDays": "3 days",
  "search.placeholder": "Search",
  "search.none": "No event found.",
  "cal.toggleStrip": "Show more weeks",
  "settings.display": "Display",
  "display.startView": "View at startup",
  "display.startAuto": "Week (boxes on a phone)",
  "display.theme": "Theme",
  "display.theme.system": "Same as the device",
  "display.theme.light": "Light",
  "display.theme.dark": "Dark",
  "display.weekNumbers": "Show week numbers",
  "display.showBlocks": "Show cross-blocking blocks (faded) instead of hiding them",
  "profile.timeFormat": "Time format",
  "profile.timeAuto": "Same as the language",
  "data.title": "My data",
  "data.exportBody": "Download everything Maki Calendar stores about you, as a JSON file. Passwords and account access keys aren't included.",
  "data.export": "Download my data",
  "data.delete": "Delete my account",
  "data.deleteWarning": "Your account, your connections, shares, booking page and bookings are deleted for good. Nothing is deleted at Google, Microsoft or on your CalDAV server; Maki Calendar's access to them is revoked.",
  "sessions.title": "Open sessions",
  "sessions.current": "This device",
  "sessions.lastSeen": "active {when}",
  "sessions.revoke": "Sign out",
  "sessions.revokeOthers": "Sign out everywhere else",
  "totp.title": "Two-step verification",
  "totp.intro": "After your password, Maki Calendar asks for a code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).",
  "totp.setup": "Turn on two-step verification",
  "totp.step1": "Scan this QR code with your authenticator app.",
  "totp.step2": "Enter the 6-digit code it shows.",
  "totp.manual": "Can't scan? Enter this key:",
  "totp.code": "Code",
  "totp.enable": "Turn on",
  "totp.on": "Two-step verification is on",
  "totp.codesLeft": "{n} backup codes left",
  "totp.newCodes": "New backup codes",
  "totp.disable": "Turn off",
  "totp.saveCodes": "Keep these backup codes somewhere safe. Each one lets you sign in once if you lose your phone. They won't be shown again.",
  "totp.copyCodes": "Copy the codes",
  "totp.loginBody": "Enter the code from your authenticator app.",
  "totp.backupHint": "You can also enter one of your backup codes.",
  "settings.blocking": "Cross-blocking",
  "blc.intro": "Keep calendars in step: every busy event in the source calendars creates a private “Busy” block in the target calendar. Colleagues who only see that calendar then know you're not free. Blocks follow their source when it moves or disappears.",
  "blc.new": "New rule",
  "blc.edit": "Edit the rule",
  "blc.none": "No rule yet.",
  "blc.count": "{n} blocks",
  "blc.lastRun": "updated {when}",
  "blc.sources": "Source calendars",
  "blc.sourcesHint": "Their busy events (not marked free, not declined) create blocks.",
  "blc.pickSource": "Choose at least one source calendar.",
  "blc.target": "Target calendar",
  "blc.targetHint": "Blocks are created here, marked private and busy, with no guests and no description.",
  "blc.title": "Block title",
  "blc.titleHint": "Busy, for example.",
  "blc.window": "Block ahead",
  "blc.allDay": "Include all-day events",
  "blc.active": "Rule active",
  "blc.deleteConfirm": "Delete this rule?",
  "blc.removeBlocks": "Also delete the {n} blocks already created",
  "share.resend": "Resend the invitation",
  "share.resent": "Invitation sent again",
  "settings.booking": "Booking",
  "bk.intro": "Let people book a time with you. Only free slots across all calendars that count as busy are offered.",
  "bk.page": "Booking page",
  "bk.address": "Page address",
  "bk.copyLink": "Copy link",
  "bk.copied": "Copied",
  "bk.slugFirst": "Choose your page address first.",
  "bk.types": "Appointment types",
  "bk.newType": "New appointment type",
  "bk.minutes": "{n} min",
  "bk.inactive": "Off",
  "bk.loc.video": "Video call",
  "bk.loc.place": "In person",
  "bk.loc.phone": "Phone call",
  "bk.loc.none": "No location",
  "bk.name": "Name",
  "bk.typeAddress": "Link address",
  "bk.slugUsed": "Another appointment type uses this address.",
  "bk.duration": "Duration",
  "bk.interval": "Start times",
  "bk.every": "every {n} min",
  "bk.color": "Color",
  "bk.destination": "Add to calendar",
  "bk.schedule": "Hours",
  "bk.location": "Location",
  "bk.noVideo": "Video calls are added for Google and Microsoft calendars only.",
  "bk.bufferBefore": "Buffer before",
  "bk.bufferAfter": "Buffer after",
  "bk.notice": "Minimum notice",
  "bk.horizon": "Book up to",
  "bk.dailyLimit": "Limit per day",
  "bk.noLimit": "No limit",
  "bk.inMinutes": "minutes",
  "bk.inHours": "hours",
  "bk.inDays": "days ahead",
  "bk.questions": "Questions",
  "bk.questionsHint": "Name and email are always asked. Add your own questions here.",
  "bk.questionLabel": "Question",
  "bk.questionType": "Answer type",
  "bk.q.text": "Short text",
  "bk.q.textarea": "Long text",
  "bk.q.choice": "Choice",
  "bk.q.checkbox": "Checkbox",
  "bk.required": "Required",
  "bk.removeQuestion": "Remove question",
  "bk.optionsHint": "Choices, separated by commas",
  "bk.addQuestion": "Add a question",
  "bk.active": "Bookable",
  "bk.deleteConfirm": "Delete this appointment type? Existing bookings stay in your calendar.",
  "bk.schedules": "Hours",
  "bk.scheduleName": "Name",
  "bk.closed": "Closed",
  "bk.addRange": "Add hours",
  "bk.removeRange": "Remove hours",
  "bk.overrides": "Date exceptions",
  "bk.overridesHint": "Close a day or change its hours, for holidays for example.",
  "bk.overrideDate": "Date",
  "bk.addOverride": "Add a date",
  "bk.removeOverride": "Remove date",
  "bk.bookings": "Bookings",
  "bk.upcoming": "Upcoming",
  "bk.past": "Past",
  "bk.noBookings": "No bookings yet.",
  "bk.cancelled": "Cancelled",
  "bk.cancelBooking": "Cancel booking",
  "bk.cancelConfirm": "The visitor will be told.",
  "pub.bookWith": "Book a time with",
  "pub.noTypes": "No appointment types are available right now.",
  "pub.notFound": "This page doesn't exist",
  "pub.notFoundBody": "Check the link you received.",
  "pub.loc.video": "Video call (link sent by email)",
  "pub.loc.phone": "Phone call",
  "pub.loc.none": "Details sent by email",
  "pub.timezone": "Time zone",
  "pub.noSlots": "No free times this month. Try the next one.",
  "pub.otherTime": "Choose another time",
  "pub.phone": "Phone number",
  "pub.confirm": "Confirm the booking",
  "pub.confirmed": "You're booked",
  "pub.confirmedBody": "A confirmation was sent to {email}, with the calendar invitation.",
  "pub.manage": "Change or cancel",
  "pub.isCancelled": "This appointment was cancelled.",
  "pub.reschedule": "Choose another time",
  "pub.cancel": "Cancel the appointment",
  "pub.cancelConfirm": "Cancel this appointment?",
  "pub.cancelledOk": "The appointment is cancelled.",
  "pub.movedOk": "The appointment has been moved.",
  "pub.bookAgain": "Book again",
  "error.SLUG_TAKEN": "This address is already used. Choose another one.",
  "error.SLOT_TAKEN": "This time was just taken. Choose another one.",
  "settings.sharing": "Sharing",
  "share.busy": "Busy",
  "share.withMe": "Shared with me",
  "share.manage": "Manage sharing",
  "share.intro": "Share your consolidated calendar with other people. For each person and each calendar, choose what they see. Changes apply immediately.",
  "share.invite": "Share my calendar",
  "share.sent": "{email} has been invited.",
  "share.send": "Share",
  "share.help.busy": "they see the times, marked “Busy”. Nothing else leaves the server.",
  "share.help.details": "they also see the title, place, description and guests.",
  "share.help.hidden": "the calendar doesn't appear at all.",
  "share.mine": "My shares",
  "share.mineEmpty": "You haven't shared your calendar yet.",
  "share.withMeEmpty": "Nobody has shared a calendar with you yet.",
  "share.pending": "Waiting for the account to be created",
  "share.active": "Active",
  "share.visibility": "Visibility",
  "share.privateNote": "Events marked private at Google, Microsoft or on the CalDAV server always appear as busy. A calendar added later uses its default visibility.",
  "share.revokeConfirm": "Stop sharing with {name}? They lose access right away.",
  "share.revoke": "Stop sharing",
  "share.label": "Name shown",
  "share.show": "Show",
  "error.SHARE_EXISTS": "Your calendar is already shared with this person.",
  "ev.new": "New event",
  "ev.edit": "Edit",
  "ev.delete": "Delete",
  "ev.create": "Create",
  "ev.saving": "Saving…",
  "ev.saved": "Saved",
  "ev.deleted": "Event deleted",
  "ev.moveFailed": "The event couldn't be moved.",
  "ev.cannotEdit": "This calendar is read-only: you can't change its events here.",
  "ev.titlePlaceholder": "Add a title",
  "ev.starts": "Starts",
  "ev.ends": "Ends",
  "ev.endBeforeStart": "The end must be after the start.",
  "ev.conflict": "Overlaps with {list}.",
  "ev.repeat": "Repeat",
  "ev.repeat.none": "Does not repeat",
  "ev.repeat.daily": "Every day",
  "ev.repeat.weekly": "Every week",
  "ev.repeat.monthly": "Every month",
  "ev.repeat.yearly": "Every year",
  "ev.repeat.every": "Every",
  "ev.repeat.unit.daily": "day(s)",
  "ev.repeat.unit.weekly": "week(s)",
  "ev.repeat.unit.monthly": "month(s)",
  "ev.repeat.unit.yearly": "year(s)",
  "ev.repeat.on": "On",
  "ev.repeat.ends": "Ends",
  "ev.repeat.never": "Never",
  "ev.repeat.onDate": "On a date",
  "ev.repeat.after": "After",
  "ev.repeat.times": "times",
  "ev.repeat.locked": "This event repeats. To change how it repeats, choose “This event and the following ones” or “All events in the series” below.",
  "ev.placePlaceholder": "Add a place or an address",
  "ev.placeAttribution": "Places © OpenStreetMap contributors",
  "ev.meet": "Add Google Meet video conferencing",
  "ev.teams": "Add a Microsoft Teams meeting",
  "ev.videoLink": "Video link",
  "ev.guestsPlaceholder": "Add guests by email",
  "ev.removeGuest": "Remove {email}",
  "ev.invitesNote": "Guests receive the invitation from your calendar service.",
  "ev.availability": "Show as",
  "ev.busy": "Busy",
  "ev.freeOption": "Free",
  "ev.reminder": "Reminder",
  "ev.reminder.default": "Calendar default",
  "ev.reminder.atStart": "At the start",
  "ev.reminder.minutes": "{n} minutes before",
  "ev.reminder.hour": "1 hour before",
  "ev.reminder.2hours": "2 hours before",
  "ev.reminder.day": "1 day before",
  "ev.privateToggle": "Private",
  "ev.privateHint": "people you share your calendar with only see that you're busy.",
  "ev.description": "Description",
  "ev.scope": "Apply to",
  "ev.scope.this": "This event",
  "ev.scope.all": "All events in the series",
  "ev.confirmDelete": "Delete this event?",
  "ev.deleteNotifies": "Guests will be told the event is cancelled.",
  "error.READ_ONLY": "This calendar is read-only.",
  "error.EVENT_REJECTED": "The calendar service refused the change.",
  "error.PROVIDER_CONFLICT": "The event changed elsewhere. Close it and try again.",
  "error.PROVIDER_NOT_CONFIGURED": "This connection isn't set up on this instance yet.",
  "error.PROVIDER_AUTH": "The server refused the username or password.",
  "error.PROVIDER_UNREACHABLE": "The server can't be reached. Check the address and try again.",
  "error.PROVIDER_INVALID": "No usable calendar was found at this address. Check it and try again.",
} as const;

export type MessageKey = keyof typeof en;
type Dict = Record<MessageKey, string>;

const fr: Dict = {
  "lang.name": "Français",
  "common.language": "Langue",
  "common.email": "E-mail",
  "common.password": "Mot de passe",
  "common.name": "Nom",
  "common.save": "Enregistrer",
  "common.saved": "Enregistré",
  "common.cancel": "Annuler",
  "common.continue": "Continuer",
  "common.back": "Retour",
  "common.loading": "Chargement…",
  "common.passwordHint": "Au moins 10 caractères.",
  "common.backToLogin": "Retour à la connexion",
  "login.title": "Connexion",
  "login.remember": "Rester connecté",
  "login.submit": "Se connecter",
  "login.forgot": "Mot de passe oublié ?",
  "login.noAccount": "Pas encore de compte ?",
  "login.create": "Créer un compte",
  "signup.title": "Créez votre compte",
  "signup.invited": "Vous avez été invité avec l'adresse {email}.",
  "signup.submit": "Créer mon compte",
  "signup.haveAccount": "Vous avez déjà un compte ?",
  "signup.closed": "Les inscriptions sont fermées sur cette instance.",
  "signup.inviteOnly": "Cette instance fonctionne sur invitation. Demandez un lien d'invitation à son administrateur.",
  "signup.checkTitle": "Consultez votre boîte de réception",
  "signup.checkBody": "Nous avons envoyé un lien de confirmation à {email}. Il est valable 24 heures.",
  "signup.resend": "Renvoyer le lien",
  "signup.resent": "Si l'adresse attend une confirmation, un nouveau lien est en route.",
  "verify.title": "Confirmation de votre adresse",
  "verify.working": "Un instant…",
  "forgot.title": "Réinitialiser votre mot de passe",
  "forgot.body": "Saisissez votre e-mail. Si un compte existe, vous recevrez un lien valable 1 heure.",
  "forgot.submit": "Envoyer le lien",
  "forgot.sent": "Si un compte existe pour cette adresse, un lien est en route.",
  "reset.title": "Choisissez un nouveau mot de passe",
  "reset.submit": "Enregistrer le mot de passe",
  "setup.title": "Installer Maki Calendar",
  "setup.intro": "Trois étapes : votre compte administrateur, l'envoi des e-mails et qui peut s'inscrire.",
  "setup.step.admin": "Administrateur",
  "setup.step.mail": "E-mails",
  "setup.step.access": "Inscriptions",
  "setup.brevoKey": "Clé API Brevo",
  "setup.brevoHelp": "Dans Brevo, ouvrez SMTP & API, puis Clés API. Authentifiez aussi votre domaine d'envoi (SPF et DKIM) pour éviter les spams.",
  "setup.fromEmail": "Adresse d'expédition",
  "setup.fromName": "Nom de l'expéditeur",
  "setup.timezone": "Fuseau horaire par défaut",
  "setup.finish": "Terminer l'installation",
  "setup.noToken": "Ce lien d'installation est incomplet. Ouvrez le lien complet affiché par le script d'installation.",
  "setup.done": "L'installation est terminée.",
  "mode.label": "Qui peut créer un compte ?",
  "mode.invite": "Seulement les personnes invitées",
  "mode.invite.help": "Vous invitez les personnes par e-mail. Recommandé.",
  "mode.open": "Tout le monde",
  "mode.open.help": "Toute personne qui connaît l'adresse peut s'inscrire.",
  "mode.closed": "Personne",
  "mode.closed.help": "Aucun nouveau compte, même avec une invitation.",
  "nav.calendar": "Agenda",
  "nav.settings": "Réglages",
  "nav.logout": "Se déconnecter",
  "home.hello": "Bonjour, {name}",
  "home.emptyTitle": "Connectez votre premier agenda",
  "home.emptyBody": "Votre compte est prêt. La connexion des agendas Google, Microsoft 365, CalDAV et iCal arrive dans la prochaine version.",
  "home.soon": "Prochaine version",
  "settings.title": "Réglages",
  "settings.account": "Mon compte",
  "settings.security": "Sécurité",
  "settings.instance": "Instance",
  "settings.users": "Utilisateurs",
  "settings.adminOnly": "Administrateur",
  "profile.timezone": "Fuseau horaire",
  "profile.weekStart": "Premier jour de la semaine",
  "profile.home": "Ville par défaut",
  "profile.homePlaceholder": "Bruxelles, par exemple",
  "profile.homeHint": "Les suggestions de lieux sont classées autour de votre position actuelle, ou autour de cette ville quand elle est inconnue.",
  "day.1": "Lundi",
  "day.0": "Dimanche",
  "day.6": "Samedi",
  "password.current": "Mot de passe actuel",
  "password.new": "Nouveau mot de passe",
  "password.change": "Changer le mot de passe",
  "password.changed": "Mot de passe changé. Vos autres sessions ont été déconnectées.",
  "instance.mail": "E-mails (Brevo)",
  "instance.keySaved": "Une clé est enregistrée. Laissez vide pour la garder.",
  "instance.test": "Envoyer un e-mail de test",
  "instance.testSent": "E-mail de test envoyé à {email}.",
  "users.invite": "Inviter quelqu'un",
  "users.inviteSubmit": "Envoyer l'invitation",
  "users.invited": "Invitation envoyée à {email}.",
  "users.disable": "Désactiver",
  "users.enable": "Réactiver",
  "users.delete": "Supprimer",
  "users.confirmDelete": "Supprimer le compte de {name} ? C'est définitif.",
  "users.you": "vous",
  "users.admin": "administrateur",
  "users.unverified": "e-mail non confirmé",
  "users.disabled": "désactivé",
  "error.INVALID_CREDENTIALS": "E-mail ou mot de passe incorrect.",
  "error.EMAIL_NOT_VERIFIED": "Confirmez d'abord votre adresse e-mail avec le lien reçu.",
  "error.ACCOUNT_DISABLED": "Ce compte a été désactivé par l'administrateur.",
  "error.EMAIL_TAKEN": "Un compte existe déjà avec cette adresse.",
  "error.REGISTRATION_CLOSED": "Les inscriptions sont fermées sur cette instance.",
  "error.INVITATION_REQUIRED": "Utilisez l'adresse e-mail qui a reçu l'invitation.",
  "error.INVALID_TOKEN": "Ce lien n'est plus valable. Demandez-en un nouveau.",
  "error.SETUP_DONE": "L'installation est déjà terminée. Connectez-vous.",
  "error.SETUP_TOKEN_INVALID": "Le lien d'installation est incorrect. Utilisez celui affiché par le script.",
  "error.SETUP_TOKEN_MISSING": "SETUP_TOKEN est absent du fichier .env du serveur.",
  "error.MAIL_NOT_CONFIGURED": "L'envoi d'e-mails n'est pas encore configuré. L'administrateur peut le faire dans les réglages.",
  "error.MAIL_FAILED": "L'e-mail n'a pas pu être envoyé. Vérifiez la clé Brevo et l'adresse d'expédition.",
  "error.RATE_LIMITED": "Trop de tentatives. Attendez une minute et réessayez.",
  "error.VALIDATION": "Certains champs sont à corriger.",
  "error.NETWORK": "Impossible de joindre le serveur. Vérifiez votre connexion.",
  "error.UNAUTHENTICATED": "Votre session a expiré. Reconnectez-vous.",
  "error.FORBIDDEN": "Seul l'administrateur peut faire cela.",
  "error.CSRF": "La requête a été refusée. Rechargez la page et réessayez.",
  "error.INTERNAL": "Un problème est survenu sur le serveur. Réessayez dans un instant.",
  "error.field": "Vérifiez ce champ.",
  "settings.calendars": "Agendas",
  "cal.lead": "Ajoutez autant de comptes que vous voulez. Pour chaque agenda, choisissez sa couleur, s'il s'affiche, s'il bloque votre page de réservation et ce que voient par défaut les personnes avec qui vous partagez.",
  "cal.add": "Ajouter un compte",
  "cal.google.sub": "Gmail ou Google Workspace",
  "cal.microsoft.sub": "Outlook, professionnel ou personnel",
  "cal.caldav.sub": "Nextcloud, iCloud, Fastmail…",
  "cal.ics": "Lien iCal",
  "cal.ics.sub": "Abonnement en lecture seule",
  "cal.notConfigured": "L'administrateur doit d'abord configurer l'application {provider} dans Réglages, Instance.",
  "cal.continueWith": "Continuer avec {provider}",
  "cal.redirectNote": "Vous allez être redirigé vers {provider} pour autoriser Maki Calendar à lire et modifier votre agenda. Aucun accès à vos e-mails n'est demandé.",
  "cal.service": "Service",
  "cal.serverUrl": "Adresse du serveur",
  "cal.username": "Identifiant",
  "cal.appPassword": "Mot de passe d'application",
  "cal.appPasswordHint": "Utilisez un mot de passe d'application plutôt que votre mot de passe principal. Il est obligatoire pour iCloud.",
  "cal.connect": "Connecter et trouver les agendas",
  "cal.icsUrl": "Lien de l'agenda",
  "cal.icsName": "Nom affiché",
  "cal.subscribe": "S'abonner",
  "cal.other": "Autre serveur",
  "cal.status.ok": "Synchronisé {when}",
  "cal.status.never": "En attente de la première synchronisation",
  "cal.status.syncing": "Synchronisation…",
  "cal.status.error": "Problème de synchronisation : {error}",
  "cal.status.reauth": "L'accès a été refusé. Reconnectez le compte.",
  "cal.sync": "Synchroniser",
  "cal.reconnect": "Reconnecter",
  "cal.remove": "Retirer",
  "cal.removeConfirm": "Retirer {label} ? Ses agendas disparaissent de Maki Calendar. Rien n'est supprimé chez le fournisseur.",
  "cal.removeOk": "Retirer le compte",
  "cal.keep": "Garder",
  "cal.show": "Afficher",
  "cal.blocks": "Bloque la réservation",
  "cal.shareDefault": "Partagé en",
  "cal.default": "Par défaut",
  "cal.readOnly": "lecture seule",
  "cal.color": "Couleur de {name}",
  "cal.connected": "Compte {provider} connecté. Ses agendas se synchronisent.",
  "cal.added": "Compte ajouté. Ses agendas se synchronisent.",
  "cal.empty": "Aucun compte connecté pour l'instant.",
  "vis.hidden": "Masqué",
  "vis.busy": "Occupé",
  "vis.details": "Détails",
  "oauth.not_configured": "Cette connexion n'est pas encore configurée. L'administrateur doit configurer l'application dans Réglages, Instance.",
  "oauth.cancelled": "Connexion annulée.",
  "oauth.expired": "La connexion a pris trop de temps. Réessayez.",
  "oauth.failed": "La connexion a échoué. Réessayez.",
  "oauth.admin_consent": "Votre organisation exige qu'un administrateur approuve Maki Calendar. Envoyez ce lien à votre administrateur Microsoft 365 :",
  "instance.providers": "Applications d'agenda (OAuth)",
  "instance.providersHelp": "Créez les applications chez Google et Microsoft, puis collez ici leurs identifiants. Chaque application doit contenir l'URI de redirection indiquée ci-dessous.",
  "instance.clientId": "Client ID",
  "instance.clientSecret": "Secret client",
  "instance.secretSaved": "Un secret est enregistré. Laissez vide pour le garder.",
  "instance.redirectUri": "URI de redirection",
  "home.upcoming": "14 prochains jours",
  "home.nothing": "Rien de prévu.",
  "home.manage": "Gérer les agendas",
  "home.addFirst": "Ajouter un agenda",
  "home.attention": "Un compte demande votre attention.",
  "home.connectBody": "Connectez Google, Microsoft 365, CalDAV (Nextcloud, iCloud…) ou un lien iCal pour tout voir au même endroit.",
  "common.allDay": "Journée",
  "common.join": "Rejoindre",
  "common.today": "Aujourd'hui",
  "common.tomorrow": "Demain",
  "view.week": "Semaine",
  "view.month": "Mois",
  "view.agenda": "Agenda",
  "view.boxes": "Cases",
  "view.label": "Vue",
  "cal.prev": "Précédent",
  "cal.next": "Suivant",
  "cal.weekNumber": "Semaine {n}",
  "cal.calendars": "Agendas",
  "cal.toggleSidebar": "Agendas et mini-calendrier",
  "cal.more": "+{n} autres",
  "cal.earlier": "Afficher les jours précédents",
  "cal.later": "Afficher les jours suivants",
  "cal.windowEnd": "Les agendas sont synchronisés jusqu'à 18 mois à l'avance.",
  "cal.windowStart": "Les agendas sont synchronisés à partir de 6 mois en arrière.",
  "cal.noTitle": "(Sans titre)",
  "cal.shortcuts": "Raccourcis : N nouvel événement, T aujourd'hui, J/K ou ← → précédent et suivant, D 3 W M L B pour changer de vue, / rechercher.",
  "ev.free": "Indiqué comme libre",
  "ev.tentative": "Provisoire",
  "ev.private": "Privé",
  "ev.recurring": "Récurrent",
  "ev.declined": "Refusé",
  "ev.guests": "Invités",
  "ev.organizer": "Organisateur",
  "ev.status.accepted": "Accepté",
  "ev.status.declined": "Refusé",
  "ev.status.tentative": "Peut-être",
  "ev.status.needs-action": "En attente de réponse",
  "ev.openMap": "Ouvrir dans une carte",
  "ev.close": "Fermer",
  "ev.notes": "Notes",
  "ev.location": "Lieu",
  "cal.calendar": "Agenda",
  "guide.google": "Comment créer l'application Google",
  "guide.google.1": "Ouvrez console.cloud.google.com et créez un projet (par exemple « Maki Calendar »).",
  "guide.google.2": "Dans API et services, Bibliothèque, activez « Google Calendar API ».",
  "guide.google.3": "Ouvrez Google Auth Platform (écran de consentement OAuth), Commencer : nom de l'application, e-mail d'assistance, audience « Externes », e-mail de contact.",
  "guide.google.4": "Dans Branding : page d'accueil {home}, règles de confidentialité {privacy}, conditions d'utilisation {terms}, domaine autorisé {domain}.",
  "guide.google.5": "Dans Accès aux données, ajoutez les niveaux d'accès openid, email, profile et https://www.googleapis.com/auth/calendar.",
  "guide.google.6": "Dans Clients, Créer un client, type « Application Web », URI de redirection autorisé : l'adresse affichée ci-dessus.",
  "guide.google.7": "Collez l'ID client et le code secret ici, puis Enregistrer.",
  "guide.google.8": "Dans Audience, Publier l'application. Pour retirer l'avertissement « application non validée », demandez la vérification dans le Centre de validation : elle utilise les pages ci-dessus et une courte vidéo de démonstration.",
  "guide.microsoft": "Comment créer l'application Microsoft",
  "guide.microsoft.1": "Connectez-vous à entra.microsoft.com avec un compte qui a son propre annuaire (un compte Azure gratuit en crée un).",
  "guide.microsoft.2": "Inscriptions d'applications, Nouvelle inscription. Comptes pris en charge : « Comptes dans un annuaire d'organisation et comptes Microsoft personnels ». URI de redirection, type Web : l'adresse affichée ci-dessus.",
  "guide.microsoft.3": "Copiez l'ID d'application (client).",
  "guide.microsoft.4": "Certificats et secrets, Nouveau secret client (24 mois). Copiez la Valeur, pas l'ID du secret, et notez la date d'expiration.",
  "guide.microsoft.5": "Autorisations de l'API, Ajouter, Microsoft Graph, Déléguées : openid, profile, email, offline_access, User.Read, Calendars.ReadWrite. N'accordez pas le consentement administrateur.",
  "guide.microsoft.6": "Collez l'ID et le secret ici, puis Enregistrer.",
  "guide.microsoft.7": "Facultatif : pour éviter « approbation de l'administrateur requise » dans certaines organisations, devenez éditeur vérifié (Personnalisation et propriétés) avec un identifiant Microsoft Cloud Partner Program et un domaine vérifié.",
  "legal.privacy": "Politique de confidentialité",
  "legal.terms": "Conditions d'utilisation",
  "instance.legal": "Pages légales",
  "instance.legalHelp": "Google demande ces pages pour vérifier votre application. Elles sont générées d'après ce que fait réellement Maki Calendar ; vous pouvez les remplacer.",
  "instance.legalOperator": "Éditeur (personne ou société)",
  "instance.legalContact": "E-mail de contact",
  "instance.legalEdit": "Modifier le texte",
  "instance.legalGenerated": "Texte généré en usage.",
  "instance.legalReset": "Revenir au texte généré",
  "settings.status": "État",
  "status.version": "Version",
  "status.since": "en marche depuis {when}",
  "status.database": "Base de données",
  "status.mail": "Envoi des e-mails",
  "status.push": "Synchronisation immédiate",
  "status.pushOff": "Désactivée : l'adresse doit être publique en HTTPS.",
  "status.users": "Utilisateurs",
  "status.connections": "Comptes connectés",
  "status.failing": "Comptes en difficulté",
  "status.noFailing": "Aucun compte en difficulté.",
  "status.backups": "Sauvegardes",
  "status.noBackups": "Aucune sauvegarde listée pour l'instant. Elles ont lieu chaque nuit à 3 h 15.",
  "status.restore": "Restaurer : sudo ./scripts/restore.sh /var/backups/confluence/<fichier>",
  "status.errors": "Erreurs récentes",
  "status.noErrors": "Aucune erreur récente.",
  "status.ok": "OK",
  "status.problem": "Problème",
  "bk.busyCalendars": "Ces agendas bloquent votre page de réservation : {list}. Modifiez « Compte comme occupé » dans Agendas.",
  "bk.busyNone": "Aucun agenda ne compte comme occupé : toutes les heures de vos horaires sont proposées. Activez « Compte comme occupé » dans Agendas.",
  "ev.scope.following": "Cet événement et les suivants",
  "ev.repeat.custom": "Cette série utilise une règle que le formulaire ne sait pas afficher (par exemple « le 2e mardi du mois »). Modifiez-la dans Google, Microsoft ou votre application CalDAV.",
  "instance.places": "Recherche de lieux",
  "instance.placesHelp": "Par défaut, les lieux viennent d'OpenStreetMap (gratuit, sans clé). Avec une clé API Google Places, les suggestions viennent de Google, qui connaît davantage de commerces et de restaurants. Google facture les recherches au-delà de son quota mensuel gratuit.",
  "instance.placesKey": "Clé API Google Places",
  "instance.placesSaved": "Une clé est enregistrée. Laissez vide pour la garder.",
  "instance.placesNone": "Pas de clé : OpenStreetMap est utilisé.",
  "instance.placesRemove": "Retirer la clé et revenir à OpenStreetMap",
  "settings.notifications": "Notifications",
  "notify.intro": "Choisissez les e-mails que Maki Calendar vous envoie. Les visiteurs reçoivent toujours la confirmation de leur réservation.",
  "notify.bookings": "Réservations",
  "notify.bookings.hint": "Un e-mail à chaque nouvelle réservation, annulation ou report sur votre page de réservation.",
  "notify.shares": "Nouveaux partages",
  "notify.shares.hint": "Un e-mail quand quelqu'un partage son agenda avec vous.",
  "notify.reconnect": "Comptes à reconnecter",
  "notify.reconnect.hint": "Un e-mail quand un compte ne s'est pas synchronisé depuis plus de 24 heures.",
  "notify.dailyRecap": "Récapitulatif du lendemain",
  "notify.dailyRecap.hint": "Chaque soir à 18 h, la liste de vos événements du lendemain.",
  "view.day": "Jour",
  "view.threeDays": "3 jours",
  "search.placeholder": "Rechercher",
  "search.none": "Aucun événement trouvé.",
  "cal.toggleStrip": "Afficher plus de semaines",
  "settings.display": "Affichage",
  "display.startView": "Vue au démarrage",
  "display.startAuto": "Semaine (cases sur téléphone)",
  "display.theme": "Thème",
  "display.theme.system": "Comme l'appareil",
  "display.theme.light": "Clair",
  "display.theme.dark": "Sombre",
  "display.weekNumbers": "Afficher les numéros de semaine",
  "display.showBlocks": "Afficher les blocs du blocage croisé (estompés) au lieu de les masquer",
  "profile.timeFormat": "Format de l'heure",
  "profile.timeAuto": "Selon la langue",
  "data.title": "Mes données",
  "data.exportBody": "Téléchargez tout ce que Maki Calendar enregistre à votre sujet, en fichier JSON. Les mots de passe et les clés d'accès aux comptes ne sont pas inclus.",
  "data.export": "Télécharger mes données",
  "data.delete": "Supprimer mon compte",
  "data.deleteWarning": "Votre compte, vos connexions, partages, page de réservation et réservations sont supprimés définitivement. Rien n'est supprimé chez Google, Microsoft ni sur votre serveur CalDAV ; l'accès de Maki Calendar y est révoqué.",
  "sessions.title": "Sessions ouvertes",
  "sessions.current": "Cet appareil",
  "sessions.lastSeen": "active {when}",
  "sessions.revoke": "Déconnecter",
  "sessions.revokeOthers": "Déconnecter partout ailleurs",
  "totp.title": "Double authentification",
  "totp.intro": "Après le mot de passe, Maki Calendar demande un code d'une application d'authentification (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).",
  "totp.setup": "Activer la double authentification",
  "totp.step1": "Scannez ce QR code avec votre application d'authentification.",
  "totp.step2": "Saisissez le code à 6 chiffres qu'elle affiche.",
  "totp.manual": "Impossible de scanner ? Saisissez cette clé :",
  "totp.code": "Code",
  "totp.enable": "Activer",
  "totp.on": "La double authentification est active",
  "totp.codesLeft": "{n} codes de secours restants",
  "totp.newCodes": "Nouveaux codes de secours",
  "totp.disable": "Désactiver",
  "totp.saveCodes": "Gardez ces codes de secours en lieu sûr. Chacun permet de vous connecter une fois si vous perdez votre téléphone. Ils ne seront plus affichés.",
  "totp.copyCodes": "Copier les codes",
  "totp.loginBody": "Saisissez le code de votre application d'authentification.",
  "totp.backupHint": "Vous pouvez aussi saisir un de vos codes de secours.",
  "settings.blocking": "Blocage croisé",
  "blc.intro": "Gardez vos agendas alignés : chaque événement occupé des agendas sources crée un bloc privé « Occupé » dans l'agenda cible. Les collègues qui ne voient que cet agenda savent alors que vous n'êtes pas libre. Les blocs suivent leur source quand elle bouge ou disparaît.",
  "blc.new": "Nouvelle règle",
  "blc.edit": "Modifier la règle",
  "blc.none": "Aucune règle pour l'instant.",
  "blc.count": "{n} blocs",
  "blc.lastRun": "mis à jour {when}",
  "blc.sources": "Agendas sources",
  "blc.sourcesHint": "Leurs événements occupés (ni libres, ni refusés) créent des blocs.",
  "blc.pickSource": "Choisissez au moins un agenda source.",
  "blc.target": "Agenda cible",
  "blc.targetHint": "Les blocs y sont créés, privés et occupés, sans invités ni description.",
  "blc.title": "Titre des blocs",
  "blc.titleHint": "Occupé, par exemple.",
  "blc.window": "Bloquer sur",
  "blc.allDay": "Inclure les événements d'une journée entière",
  "blc.active": "Règle active",
  "blc.deleteConfirm": "Supprimer cette règle ?",
  "blc.removeBlocks": "Supprimer aussi les {n} blocs déjà créés",
  "share.resend": "Renvoyer l'invitation",
  "share.resent": "Invitation renvoyée",
  "settings.booking": "Réservation",
  "bk.intro": "Permettez qu'on réserve un moment avec vous. Seuls les créneaux libres dans tous les agendas qui comptent comme occupé sont proposés.",
  "bk.page": "Page de réservation",
  "bk.address": "Adresse de la page",
  "bk.copyLink": "Copier le lien",
  "bk.copied": "Copié",
  "bk.slugFirst": "Choisissez d'abord l'adresse de votre page.",
  "bk.types": "Types de rendez-vous",
  "bk.newType": "Nouveau type de rendez-vous",
  "bk.minutes": "{n} min",
  "bk.inactive": "Désactivé",
  "bk.loc.video": "Visioconférence",
  "bk.loc.place": "En personne",
  "bk.loc.phone": "Appel téléphonique",
  "bk.loc.none": "Sans lieu",
  "bk.name": "Nom",
  "bk.typeAddress": "Adresse du lien",
  "bk.slugUsed": "Un autre type de rendez-vous utilise cette adresse.",
  "bk.duration": "Durée",
  "bk.interval": "Heures de début",
  "bk.every": "toutes les {n} min",
  "bk.color": "Couleur",
  "bk.destination": "Ajouter dans l'agenda",
  "bk.schedule": "Horaires",
  "bk.location": "Lieu",
  "bk.noVideo": "La visio n'est ajoutée que pour les agendas Google et Microsoft.",
  "bk.bufferBefore": "Marge avant",
  "bk.bufferAfter": "Marge après",
  "bk.notice": "Délai minimum",
  "bk.horizon": "Réservable jusqu'à",
  "bk.dailyLimit": "Limite par jour",
  "bk.noLimit": "Sans limite",
  "bk.inMinutes": "minutes",
  "bk.inHours": "heures",
  "bk.inDays": "jours à l'avance",
  "bk.questions": "Questions",
  "bk.questionsHint": "Le nom et l'e-mail sont toujours demandés. Ajoutez ici vos propres questions.",
  "bk.questionLabel": "Question",
  "bk.questionType": "Type de réponse",
  "bk.q.text": "Texte court",
  "bk.q.textarea": "Texte long",
  "bk.q.choice": "Choix",
  "bk.q.checkbox": "Case à cocher",
  "bk.required": "Obligatoire",
  "bk.removeQuestion": "Retirer la question",
  "bk.optionsHint": "Choix, séparés par des virgules",
  "bk.addQuestion": "Ajouter une question",
  "bk.active": "Réservable",
  "bk.deleteConfirm": "Supprimer ce type de rendez-vous ? Les réservations existantes restent dans votre agenda.",
  "bk.schedules": "Horaires",
  "bk.scheduleName": "Nom",
  "bk.closed": "Fermé",
  "bk.addRange": "Ajouter une plage",
  "bk.removeRange": "Retirer la plage",
  "bk.overrides": "Exceptions par date",
  "bk.overridesHint": "Fermez un jour ou changez ses horaires, pour des congés par exemple.",
  "bk.overrideDate": "Date",
  "bk.addOverride": "Ajouter une date",
  "bk.removeOverride": "Retirer la date",
  "bk.bookings": "Réservations",
  "bk.upcoming": "À venir",
  "bk.past": "Passées",
  "bk.noBookings": "Aucune réservation pour l'instant.",
  "bk.cancelled": "Annulée",
  "bk.cancelBooking": "Annuler la réservation",
  "bk.cancelConfirm": "La personne sera prévenue.",
  "pub.bookWith": "Réserver un moment avec",
  "pub.noTypes": "Aucun type de rendez-vous n'est disponible pour l'instant.",
  "pub.notFound": "Cette page n'existe pas",
  "pub.notFoundBody": "Vérifiez le lien que vous avez reçu.",
  "pub.loc.video": "Visioconférence (lien envoyé par e-mail)",
  "pub.loc.phone": "Appel téléphonique",
  "pub.loc.none": "Détails envoyés par e-mail",
  "pub.timezone": "Fuseau horaire",
  "pub.noSlots": "Aucun créneau libre ce mois-ci. Essayez le suivant.",
  "pub.otherTime": "Choisir un autre moment",
  "pub.phone": "Numéro de téléphone",
  "pub.confirm": "Confirmer la réservation",
  "pub.confirmed": "C'est réservé",
  "pub.confirmedBody": "Une confirmation a été envoyée à {email}, avec l'invitation d'agenda.",
  "pub.manage": "Modifier ou annuler",
  "pub.isCancelled": "Ce rendez-vous a été annulé.",
  "pub.reschedule": "Choisir un autre moment",
  "pub.cancel": "Annuler le rendez-vous",
  "pub.cancelConfirm": "Annuler ce rendez-vous ?",
  "pub.cancelledOk": "Le rendez-vous est annulé.",
  "pub.movedOk": "Le rendez-vous a été déplacé.",
  "pub.bookAgain": "Réserver à nouveau",
  "error.SLUG_TAKEN": "Cette adresse est déjà utilisée. Choisissez-en une autre.",
  "error.SLOT_TAKEN": "Ce créneau vient d'être pris. Choisissez-en un autre.",
  "settings.sharing": "Partage",
  "share.busy": "Occupé",
  "share.withMe": "Partagés avec moi",
  "share.manage": "Gérer les partages",
  "share.intro": "Partagez votre agenda consolidé avec d'autres personnes. Pour chaque personne et chaque agenda, choisissez ce qu'elle voit. Les changements s'appliquent immédiatement.",
  "share.invite": "Partager mon agenda",
  "share.sent": "Invitation envoyée à {email}.",
  "share.send": "Partager",
  "share.help.busy": "elle voit les horaires, marqués « Occupé ». Rien d'autre ne quitte le serveur.",
  "share.help.details": "elle voit aussi le titre, le lieu, la description et les invités.",
  "share.help.hidden": "l'agenda n'apparaît pas du tout.",
  "share.mine": "Mes partages",
  "share.mineEmpty": "Vous n'avez pas encore partagé votre agenda.",
  "share.withMeEmpty": "Personne ne vous a encore partagé d'agenda.",
  "share.pending": "En attente de création du compte",
  "share.active": "Actif",
  "share.visibility": "Visibilité",
  "share.privateNote": "Les événements marqués privés chez Google, Microsoft ou sur le serveur CalDAV restent toujours « occupé ». Un agenda ajouté plus tard prend sa visibilité par défaut.",
  "share.revokeConfirm": "Arrêter le partage avec {name} ? L'accès est retiré immédiatement.",
  "share.revoke": "Arrêter le partage",
  "share.label": "Nom affiché",
  "share.show": "Afficher",
  "error.SHARE_EXISTS": "Votre agenda est déjà partagé avec cette personne.",
  "ev.new": "Nouvel événement",
  "ev.edit": "Modifier",
  "ev.delete": "Supprimer",
  "ev.create": "Créer",
  "ev.saving": "Enregistrement…",
  "ev.saved": "Enregistré",
  "ev.deleted": "Événement supprimé",
  "ev.moveFailed": "L'événement n'a pas pu être déplacé.",
  "ev.cannotEdit": "Cet agenda est en lecture seule : ses événements ne peuvent pas être modifiés ici.",
  "ev.titlePlaceholder": "Ajouter un titre",
  "ev.starts": "Début",
  "ev.ends": "Fin",
  "ev.endBeforeStart": "La fin doit être après le début.",
  "ev.conflict": "Chevauche {list}.",
  "ev.repeat": "Répétition",
  "ev.repeat.none": "Ne se répète pas",
  "ev.repeat.daily": "Tous les jours",
  "ev.repeat.weekly": "Toutes les semaines",
  "ev.repeat.monthly": "Tous les mois",
  "ev.repeat.yearly": "Tous les ans",
  "ev.repeat.every": "Tous les",
  "ev.repeat.unit.daily": "jour(s)",
  "ev.repeat.unit.weekly": "semaine(s)",
  "ev.repeat.unit.monthly": "mois",
  "ev.repeat.unit.yearly": "an(s)",
  "ev.repeat.on": "Le",
  "ev.repeat.ends": "Fin",
  "ev.repeat.never": "Jamais",
  "ev.repeat.onDate": "À une date",
  "ev.repeat.after": "Après",
  "ev.repeat.times": "fois",
  "ev.repeat.locked": "Cet événement se répète. Pour changer sa répétition, choisissez « Cet événement et les suivants » ou « Tous les événements de la série » plus bas.",
  "ev.placePlaceholder": "Ajouter un lieu ou une adresse",
  "ev.placeAttribution": "Lieux © contributeurs OpenStreetMap",
  "ev.meet": "Ajouter une visioconférence Google Meet",
  "ev.teams": "Ajouter une réunion Microsoft Teams",
  "ev.videoLink": "Lien de visio",
  "ev.guestsPlaceholder": "Ajouter des invités par e-mail",
  "ev.removeGuest": "Retirer {email}",
  "ev.invitesNote": "Les invités reçoivent l'invitation de votre service d'agenda.",
  "ev.availability": "Afficher comme",
  "ev.busy": "Occupé",
  "ev.freeOption": "Libre",
  "ev.reminder": "Rappel",
  "ev.reminder.default": "Réglage de l'agenda",
  "ev.reminder.atStart": "Au début",
  "ev.reminder.minutes": "{n} minutes avant",
  "ev.reminder.hour": "1 heure avant",
  "ev.reminder.2hours": "2 heures avant",
  "ev.reminder.day": "1 jour avant",
  "ev.privateToggle": "Privé",
  "ev.privateHint": "les personnes avec qui vous partagez votre agenda voient seulement que vous êtes occupé.",
  "ev.description": "Description",
  "ev.scope": "Appliquer à",
  "ev.scope.this": "Cet événement",
  "ev.scope.all": "Tous les événements de la série",
  "ev.confirmDelete": "Supprimer cet événement ?",
  "ev.deleteNotifies": "Les invités seront prévenus de l'annulation.",
  "error.READ_ONLY": "Cet agenda est en lecture seule.",
  "error.EVENT_REJECTED": "Le service d'agenda a refusé la modification.",
  "error.PROVIDER_CONFLICT": "L'événement a changé ailleurs. Fermez-le et réessayez.",
  "error.PROVIDER_NOT_CONFIGURED": "Cette connexion n'est pas encore configurée sur cette instance.",
  "error.PROVIDER_AUTH": "Le serveur a refusé l'identifiant ou le mot de passe.",
  "error.PROVIDER_UNREACHABLE": "Le serveur est injoignable. Vérifiez l'adresse et réessayez.",
  "error.PROVIDER_INVALID": "Aucun agenda utilisable n'a été trouvé à cette adresse. Vérifiez-la et réessayez.",
};

const nl: Dict = {
  "lang.name": "Nederlands",
  "common.language": "Taal",
  "common.email": "E-mail",
  "common.password": "Wachtwoord",
  "common.name": "Naam",
  "common.save": "Opslaan",
  "common.saved": "Opgeslagen",
  "common.cancel": "Annuleren",
  "common.continue": "Doorgaan",
  "common.back": "Terug",
  "common.loading": "Laden…",
  "common.passwordHint": "Minstens 10 tekens.",
  "common.backToLogin": "Terug naar inloggen",
  "login.title": "Inloggen",
  "login.remember": "Ingelogd blijven",
  "login.submit": "Inloggen",
  "login.forgot": "Wachtwoord vergeten?",
  "login.noAccount": "Nog geen account?",
  "login.create": "Account aanmaken",
  "signup.title": "Maak je account aan",
  "signup.invited": "Je bent uitgenodigd met {email}.",
  "signup.submit": "Account aanmaken",
  "signup.haveAccount": "Heb je al een account?",
  "signup.closed": "Registreren is gesloten op deze instantie.",
  "signup.inviteOnly": "Deze instantie werkt alleen met uitnodigingen. Vraag de beheerder om een uitnodigingslink.",
  "signup.checkTitle": "Kijk in je inbox",
  "signup.checkBody": "We hebben een bevestigingslink gestuurd naar {email}. Die is 24 uur geldig.",
  "signup.resend": "Link opnieuw sturen",
  "signup.resent": "Als het adres op bevestiging wacht, is er een nieuwe link onderweg.",
  "verify.title": "Je adres wordt bevestigd",
  "verify.working": "Even geduld…",
  "forgot.title": "Wachtwoord opnieuw instellen",
  "forgot.body": "Vul je e-mailadres in. Als er een account bestaat, krijg je een link die 1 uur geldig is.",
  "forgot.submit": "Link sturen",
  "forgot.sent": "Als er een account bestaat voor dit adres, is er een link onderweg.",
  "reset.title": "Kies een nieuw wachtwoord",
  "reset.submit": "Wachtwoord opslaan",
  "setup.title": "Maki Calendar installeren",
  "setup.intro": "Drie stappen: je beheerdersaccount, het versturen van e-mails en wie zich kan registreren.",
  "setup.step.admin": "Beheerder",
  "setup.step.mail": "E-mails",
  "setup.step.access": "Registratie",
  "setup.brevoKey": "Brevo API-sleutel",
  "setup.brevoHelp": "Open in Brevo SMTP & API en daarna API-sleutels. Verifieer ook je afzenderdomein (SPF en DKIM) zodat e-mails niet in de spam belanden.",
  "setup.fromEmail": "Afzenderadres",
  "setup.fromName": "Naam afzender",
  "setup.timezone": "Standaardtijdzone",
  "setup.finish": "Installatie voltooien",
  "setup.noToken": "Deze installatielink is onvolledig. Open de volledige link die het installatiescript toont.",
  "setup.done": "De installatie is voltooid.",
  "mode.label": "Wie kan een account aanmaken?",
  "mode.invite": "Alleen uitgenodigde personen",
  "mode.invite.help": "Je nodigt mensen uit per e-mail. Aanbevolen.",
  "mode.open": "Iedereen",
  "mode.open.help": "Iedereen die het adres kent, kan zich registreren.",
  "mode.closed": "Niemand",
  "mode.closed.help": "Geen nieuwe accounts, ook niet met een uitnodiging.",
  "nav.calendar": "Agenda",
  "nav.settings": "Instellingen",
  "nav.logout": "Uitloggen",
  "home.hello": "Hallo, {name}",
  "home.emptyTitle": "Koppel je eerste agenda",
  "home.emptyBody": "Je account is klaar. Agenda's van Google, Microsoft 365, CalDAV en iCal koppelen komt in de volgende versie.",
  "home.soon": "Volgende versie",
  "settings.title": "Instellingen",
  "settings.account": "Mijn account",
  "settings.security": "Beveiliging",
  "settings.instance": "Instantie",
  "settings.users": "Gebruikers",
  "settings.adminOnly": "Beheerder",
  "profile.timezone": "Tijdzone",
  "profile.weekStart": "Eerste dag van de week",
  "profile.home": "Standaardstad",
  "profile.homePlaceholder": "Brussel, bijvoorbeeld",
  "profile.homeHint": "Plaatsvoorstellen worden gesorteerd rond uw huidige locatie, of rond deze stad als die onbekend is.",
  "day.1": "Maandag",
  "day.0": "Zondag",
  "day.6": "Zaterdag",
  "password.current": "Huidig wachtwoord",
  "password.new": "Nieuw wachtwoord",
  "password.change": "Wachtwoord wijzigen",
  "password.changed": "Wachtwoord gewijzigd. Je andere sessies zijn uitgelogd.",
  "instance.mail": "E-mails (Brevo)",
  "instance.keySaved": "Er is een sleutel opgeslagen. Laat leeg om die te behouden.",
  "instance.test": "Test-e-mail sturen",
  "instance.testSent": "Test-e-mail gestuurd naar {email}.",
  "users.invite": "Iemand uitnodigen",
  "users.inviteSubmit": "Uitnodiging sturen",
  "users.invited": "Uitnodiging gestuurd naar {email}.",
  "users.disable": "Uitschakelen",
  "users.enable": "Inschakelen",
  "users.delete": "Verwijderen",
  "users.confirmDelete": "Het account van {name} verwijderen? Dit kan niet ongedaan worden gemaakt.",
  "users.you": "jij",
  "users.admin": "beheerder",
  "users.unverified": "e-mail niet bevestigd",
  "users.disabled": "uitgeschakeld",
  "error.INVALID_CREDENTIALS": "Onjuist e-mailadres of wachtwoord.",
  "error.EMAIL_NOT_VERIFIED": "Bevestig eerst je e-mailadres met de link die je kreeg.",
  "error.ACCOUNT_DISABLED": "Dit account is uitgeschakeld door de beheerder.",
  "error.EMAIL_TAKEN": "Er bestaat al een account met dit adres.",
  "error.REGISTRATION_CLOSED": "Registreren is gesloten op deze instantie.",
  "error.INVITATION_REQUIRED": "Gebruik het e-mailadres dat de uitnodiging ontving.",
  "error.INVALID_TOKEN": "Deze link is niet meer geldig. Vraag een nieuwe aan.",
  "error.SETUP_DONE": "De installatie is al voltooid. Log in.",
  "error.SETUP_TOKEN_INVALID": "De installatielink klopt niet. Gebruik de link die het script toont.",
  "error.SETUP_TOKEN_MISSING": "SETUP_TOKEN ontbreekt in het .env-bestand van de server.",
  "error.MAIL_NOT_CONFIGURED": "E-mails versturen is nog niet ingesteld. De beheerder kan dat doen in Instellingen.",
  "error.MAIL_FAILED": "De e-mail kon niet worden verstuurd. Controleer de Brevo-sleutel en het afzenderadres.",
  "error.RATE_LIMITED": "Te veel pogingen. Wacht een minuut en probeer opnieuw.",
  "error.VALIDATION": "Sommige velden moeten worden verbeterd.",
  "error.NETWORK": "De server is niet bereikbaar. Controleer je verbinding.",
  "error.UNAUTHENTICATED": "Je sessie is verlopen. Log opnieuw in.",
  "error.FORBIDDEN": "Alleen de beheerder kan dit doen.",
  "error.CSRF": "Het verzoek is geweigerd. Herlaad de pagina en probeer opnieuw.",
  "error.INTERNAL": "Er ging iets mis op de server. Probeer het zo meteen opnieuw.",
  "error.field": "Controleer dit veld.",
  "settings.calendars": "Agenda's",
  "cal.lead": "Voeg zoveel accounts toe als je wilt. Kies per agenda de kleur, of hij getoond wordt, of hij je boekingspagina blokkeert en wat mensen met wie je deelt standaard zien.",
  "cal.add": "Account toevoegen",
  "cal.google.sub": "Gmail of Google Workspace",
  "cal.microsoft.sub": "Outlook, werk of privé",
  "cal.caldav.sub": "Nextcloud, iCloud, Fastmail…",
  "cal.ics": "iCal-link",
  "cal.ics.sub": "Abonnement, alleen lezen",
  "cal.notConfigured": "De beheerder moet eerst de {provider}-app instellen in Instellingen, Instantie.",
  "cal.continueWith": "Doorgaan met {provider}",
  "cal.redirectNote": "Je wordt doorgestuurd naar {provider} om Maki Calendar toegang te geven tot je agenda (lezen en bewerken). Er wordt geen toegang tot je e-mails gevraagd.",
  "cal.service": "Dienst",
  "cal.serverUrl": "Serveradres",
  "cal.username": "Gebruikersnaam",
  "cal.appPassword": "App-wachtwoord",
  "cal.appPasswordHint": "Gebruik een app-wachtwoord in plaats van je hoofdwachtwoord. Voor iCloud is dat verplicht.",
  "cal.connect": "Verbinden en agenda's zoeken",
  "cal.icsUrl": "Agendalink",
  "cal.icsName": "Weergavenaam",
  "cal.subscribe": "Abonneren",
  "cal.other": "Andere server",
  "cal.status.ok": "Gesynchroniseerd {when}",
  "cal.status.never": "Wacht op de eerste synchronisatie",
  "cal.status.syncing": "Synchroniseren…",
  "cal.status.error": "Synchronisatieprobleem: {error}",
  "cal.status.reauth": "De toegang is geweigerd. Koppel het account opnieuw.",
  "cal.sync": "Nu synchroniseren",
  "cal.reconnect": "Opnieuw koppelen",
  "cal.remove": "Verwijderen",
  "cal.removeConfirm": "{label} verwijderen? De agenda's verdwijnen uit Maki Calendar. Bij de aanbieder wordt niets verwijderd.",
  "cal.removeOk": "Account verwijderen",
  "cal.keep": "Behouden",
  "cal.show": "Tonen",
  "cal.blocks": "Blokkeert boekingen",
  "cal.shareDefault": "Gedeeld als",
  "cal.default": "Standaard",
  "cal.readOnly": "alleen lezen",
  "cal.color": "Kleur van {name}",
  "cal.connected": "{provider}-account gekoppeld. De agenda's worden gesynchroniseerd.",
  "cal.added": "Account toegevoegd. De agenda's worden gesynchroniseerd.",
  "cal.empty": "Nog geen account gekoppeld.",
  "vis.hidden": "Verborgen",
  "vis.busy": "Bezet",
  "vis.details": "Details",
  "oauth.not_configured": "Deze koppeling is nog niet ingesteld. De beheerder moet de app instellen in Instellingen, Instantie.",
  "oauth.cancelled": "Koppeling geannuleerd.",
  "oauth.expired": "De koppeling duurde te lang. Probeer opnieuw.",
  "oauth.failed": "De koppeling is mislukt. Probeer opnieuw.",
  "oauth.admin_consent": "Je organisatie vereist dat een beheerder Maki Calendar goedkeurt. Stuur deze link naar je Microsoft 365-beheerder:",
  "instance.providers": "Agenda-apps (OAuth)",
  "instance.providersHelp": "Maak de apps aan bij Google en Microsoft en plak hier hun gegevens. Elke app heeft de redirect-URI hieronder nodig.",
  "instance.clientId": "Client ID",
  "instance.clientSecret": "Clientgeheim",
  "instance.secretSaved": "Er is een geheim opgeslagen. Laat leeg om het te behouden.",
  "instance.redirectUri": "Redirect-URI",
  "home.upcoming": "Komende 14 dagen",
  "home.nothing": "Niets gepland.",
  "home.manage": "Agenda's beheren",
  "home.addFirst": "Agenda toevoegen",
  "home.attention": "Een account vraagt je aandacht.",
  "home.connectBody": "Koppel Google, Microsoft 365, CalDAV (Nextcloud, iCloud…) of een iCal-link om alles op één plek te zien.",
  "common.allDay": "Hele dag",
  "common.join": "Deelnemen",
  "common.today": "Vandaag",
  "common.tomorrow": "Morgen",
  "view.week": "Week",
  "view.month": "Maand",
  "view.agenda": "Agenda",
  "view.boxes": "Vakken",
  "view.label": "Weergave",
  "cal.prev": "Vorige",
  "cal.next": "Volgende",
  "cal.weekNumber": "Week {n}",
  "cal.calendars": "Agenda's",
  "cal.toggleSidebar": "Agenda's en minikalender",
  "cal.more": "+{n} meer",
  "cal.earlier": "Eerdere dagen tonen",
  "cal.later": "Latere dagen tonen",
  "cal.windowEnd": "Agenda's worden tot 18 maanden vooruit gesynchroniseerd.",
  "cal.windowStart": "Agenda's worden vanaf 6 maanden terug gesynchroniseerd.",
  "cal.noTitle": "(Geen titel)",
  "cal.shortcuts": "Sneltoetsen: N nieuwe afspraak, T vandaag, J/K of ← → vorige en volgende, D 3 W M L B voor de weergave, / zoeken.",
  "ev.free": "Weergegeven als vrij",
  "ev.tentative": "Voorlopig",
  "ev.private": "Privé",
  "ev.recurring": "Herhaalt",
  "ev.declined": "Geweigerd",
  "ev.guests": "Gasten",
  "ev.organizer": "Organisator",
  "ev.status.accepted": "Geaccepteerd",
  "ev.status.declined": "Geweigerd",
  "ev.status.tentative": "Misschien",
  "ev.status.needs-action": "Wacht op antwoord",
  "ev.openMap": "Openen in een kaart",
  "ev.close": "Sluiten",
  "ev.notes": "Notities",
  "ev.location": "Locatie",
  "cal.calendar": "Agenda",
  "guide.google": "Zo maakt u de Google-app",
  "guide.google.1": "Open console.cloud.google.com en maak een project (bijvoorbeeld ‘Maki Calendar’).",
  "guide.google.2": "Schakel in API's en services, Bibliotheek, ‘Google Calendar API’ in.",
  "guide.google.3": "Open Google Auth Platform (OAuth-toestemmingsscherm), Aan de slag: app-naam, ondersteuningsadres, doelgroep ‘Extern’, contactadres.",
  "guide.google.4": "In Branding: startpagina {home}, privacybeleid {privacy}, servicevoorwaarden {terms}, geautoriseerd domein {domain}.",
  "guide.google.5": "Voeg in Gegevenstoegang de bereiken openid, email, profile en https://www.googleapis.com/auth/calendar toe.",
  "guide.google.6": "In Clients, Client maken, type ‘Webapplicatie’, geautoriseerde omleidings-URI: het adres hierboven.",
  "guide.google.7": "Plak de client-ID en het geheim hier en klik op Opslaan.",
  "guide.google.8": "Klik in Doelgroep op App publiceren. Vraag in het verificatiecentrum verificatie aan om de waarschuwing ‘niet-geverifieerde app’ te verwijderen: daarvoor gebruikt Google de pagina's hierboven en een korte demovideo.",
  "guide.microsoft": "Zo maakt u de Microsoft-app",
  "guide.microsoft.1": "Meld u aan op entra.microsoft.com met een account met een eigen directory (een gratis Azure-account maakt er een aan).",
  "guide.microsoft.2": "App-registraties, Nieuwe registratie. Ondersteunde accounts: ‘Accounts in elke organisatiedirectory en persoonlijke Microsoft-accounts’. Omleidings-URI, type Web: het adres hierboven.",
  "guide.microsoft.3": "Kopieer de toepassings-ID (client).",
  "guide.microsoft.4": "Certificaten en geheimen, Nieuw clientgeheim (24 maanden). Kopieer de Waarde, niet de geheim-ID, en noteer de vervaldatum.",
  "guide.microsoft.5": "API-machtigingen, Toevoegen, Microsoft Graph, Gedelegeerd: openid, profile, email, offline_access, User.Read, Calendars.ReadWrite. Verleen geen beheerderstoestemming.",
  "guide.microsoft.6": "Plak de ID en het geheim hier en klik op Opslaan.",
  "guide.microsoft.7": "Optioneel: om ‘goedkeuring van beheerder vereist’ in sommige organisaties te vermijden, wordt u geverifieerde uitgever (Huisstijl en eigenschappen) met een Microsoft Cloud Partner Program-ID en een geverifieerd domein.",
  "legal.privacy": "Privacybeleid",
  "legal.terms": "Gebruiksvoorwaarden",
  "instance.legal": "Juridische pagina's",
  "instance.legalHelp": "Google vraagt deze pagina's om uw app te verifiëren. Ze zijn gegenereerd op basis van wat Maki Calendar werkelijk doet; u kunt ze vervangen.",
  "instance.legalOperator": "Uitgever (persoon of bedrijf)",
  "instance.legalContact": "Contactadres",
  "instance.legalEdit": "Tekst bewerken",
  "instance.legalGenerated": "Gegenereerde tekst in gebruik.",
  "instance.legalReset": "Terug naar de gegenereerde tekst",
  "settings.status": "Status",
  "status.version": "Versie",
  "status.since": "actief sinds {when}",
  "status.database": "Database",
  "status.mail": "E-mails versturen",
  "status.push": "Directe synchronisatie",
  "status.pushOff": "Uit: het adres moet openbaar en HTTPS zijn.",
  "status.users": "Gebruikers",
  "status.connections": "Gekoppelde accounts",
  "status.failing": "Accounts met een probleem",
  "status.noFailing": "Geen account met een probleem.",
  "status.backups": "Back-ups",
  "status.noBackups": "Nog geen back-up vermeld. Ze lopen elke nacht om 3.15 uur.",
  "status.restore": "Herstellen: sudo ./scripts/restore.sh /var/backups/confluence/<bestand>",
  "status.errors": "Recente fouten",
  "status.noErrors": "Geen recente fouten.",
  "status.ok": "OK",
  "status.problem": "Probleem",
  "bk.busyCalendars": "Deze agenda's blokkeren uw boekingspagina: {list}. Wijzig ‘Telt als bezet’ in Agenda's.",
  "bk.busyNone": "Geen enkele agenda telt als bezet: alle tijden binnen uw uren worden aangeboden. Zet ‘Telt als bezet’ aan in Agenda's.",
  "ev.scope.following": "Deze en volgende afspraken",
  "ev.repeat.custom": "Deze reeks gebruikt een regel die het formulier niet kan tonen (bijvoorbeeld ‘de 2e dinsdag van de maand’). Wijzig hem in Google, Microsoft of uw CalDAV-app.",
  "instance.places": "Plaatsen zoeken",
  "instance.placesHelp": "Standaard komen plaatsen van OpenStreetMap (gratis, zonder sleutel). Met een Google Places-API-sleutel komen de voorstellen van Google, dat meer winkels en restaurants kent. Google rekent zoekopdrachten boven het gratis maandquotum aan.",
  "instance.placesKey": "Google Places-API-sleutel",
  "instance.placesSaved": "Er is een sleutel opgeslagen. Laat leeg om hem te behouden.",
  "instance.placesNone": "Geen sleutel: OpenStreetMap wordt gebruikt.",
  "instance.placesRemove": "Sleutel verwijderen en terug naar OpenStreetMap",
  "settings.notifications": "Meldingen",
  "notify.intro": "Kies welke e-mails Maki Calendar u stuurt. Bezoekers krijgen altijd de bevestiging van hun boeking.",
  "notify.bookings": "Boekingen",
  "notify.bookings.hint": "Een e-mail bij elke nieuwe boeking, annulering of wijziging op uw boekingspagina.",
  "notify.shares": "Nieuwe gedeelde agenda's",
  "notify.shares.hint": "Een e-mail wanneer iemand een agenda met u deelt.",
  "notify.reconnect": "Opnieuw te koppelen accounts",
  "notify.reconnect.hint": "Een e-mail wanneer een account meer dan 24 uur niet is gesynchroniseerd.",
  "notify.dailyRecap": "Overzicht van morgen",
  "notify.dailyRecap.hint": "Elke avond om 18 uur de lijst van uw afspraken voor de volgende dag.",
  "view.day": "Dag",
  "view.threeDays": "3 dagen",
  "search.placeholder": "Zoeken",
  "search.none": "Geen afspraak gevonden.",
  "cal.toggleStrip": "Meer weken tonen",
  "settings.display": "Weergave",
  "display.startView": "Weergave bij het openen",
  "display.startAuto": "Week (vakken op een telefoon)",
  "display.theme": "Thema",
  "display.theme.system": "Zoals het apparaat",
  "display.theme.light": "Licht",
  "display.theme.dark": "Donker",
  "display.weekNumbers": "Weeknummers tonen",
  "display.showBlocks": "Blokken van kruisblokkering tonen (vervaagd) in plaats van ze te verbergen",
  "profile.timeFormat": "Tijdnotatie",
  "profile.timeAuto": "Volgens de taal",
  "data.title": "Mijn gegevens",
  "data.exportBody": "Download alles wat Maki Calendar over u bewaart, als JSON-bestand. Wachtwoorden en toegangssleutels van accounts zitten er niet in.",
  "data.export": "Mijn gegevens downloaden",
  "data.delete": "Mijn account verwijderen",
  "data.deleteWarning": "Uw account, koppelingen, gedeelde agenda's, boekingspagina en boekingen worden definitief verwijderd. Bij Google, Microsoft of op uw CalDAV-server wordt niets verwijderd; de toegang van Maki Calendar wordt ingetrokken.",
  "sessions.title": "Open sessies",
  "sessions.current": "Dit apparaat",
  "sessions.lastSeen": "actief {when}",
  "sessions.revoke": "Afmelden",
  "sessions.revokeOthers": "Overal anders afmelden",
  "totp.title": "Tweestapsverificatie",
  "totp.intro": "Na uw wachtwoord vraagt Maki Calendar een code uit een authenticatie-app (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).",
  "totp.setup": "Tweestapsverificatie inschakelen",
  "totp.step1": "Scan deze QR-code met uw authenticatie-app.",
  "totp.step2": "Voer de 6-cijferige code in die ze toont.",
  "totp.manual": "Scannen lukt niet? Voer deze sleutel in:",
  "totp.code": "Code",
  "totp.enable": "Inschakelen",
  "totp.on": "Tweestapsverificatie is ingeschakeld",
  "totp.codesLeft": "nog {n} herstelcodes",
  "totp.newCodes": "Nieuwe herstelcodes",
  "totp.disable": "Uitschakelen",
  "totp.saveCodes": "Bewaar deze herstelcodes op een veilige plek. Met elke code kunt u één keer inloggen als u uw telefoon kwijt bent. Ze worden niet meer getoond.",
  "totp.copyCodes": "Codes kopiëren",
  "totp.loginBody": "Voer de code uit uw authenticatie-app in.",
  "totp.backupHint": "U kunt ook een van uw herstelcodes invoeren.",
  "settings.blocking": "Kruisblokkering",
  "blc.intro": "Houd uw agenda's gelijk: elke bezette afspraak in de bronagenda's maakt een privéblok ‘Bezet’ in de doelagenda. Collega's die alleen die agenda zien, weten dan dat u niet vrij bent. Blokken volgen hun bron als die verschuift of verdwijnt.",
  "blc.new": "Nieuwe regel",
  "blc.edit": "Regel bewerken",
  "blc.none": "Nog geen regels.",
  "blc.count": "{n} blokken",
  "blc.lastRun": "bijgewerkt {when}",
  "blc.sources": "Bronagenda's",
  "blc.sourcesHint": "Hun bezette afspraken (niet vrij, niet geweigerd) maken blokken.",
  "blc.pickSource": "Kies minstens één bronagenda.",
  "blc.target": "Doelagenda",
  "blc.targetHint": "Hier worden de blokken gemaakt, privé en bezet, zonder gasten of beschrijving.",
  "blc.title": "Titel van de blokken",
  "blc.titleHint": "Bezet, bijvoorbeeld.",
  "blc.window": "Blokkeren voor",
  "blc.allDay": "Hele-dagafspraken meenemen",
  "blc.active": "Regel actief",
  "blc.deleteConfirm": "Deze regel verwijderen?",
  "blc.removeBlocks": "Ook de {n} al gemaakte blokken verwijderen",
  "share.resend": "Uitnodiging opnieuw sturen",
  "share.resent": "Uitnodiging opnieuw verstuurd",
  "settings.booking": "Boeken",
  "bk.intro": "Laat anderen een moment met u boeken. Alleen vrije tijden in alle agenda's die als bezet tellen worden aangeboden.",
  "bk.page": "Boekingspagina",
  "bk.address": "Adres van de pagina",
  "bk.copyLink": "Link kopiëren",
  "bk.copied": "Gekopieerd",
  "bk.slugFirst": "Kies eerst het adres van uw pagina.",
  "bk.types": "Soorten afspraken",
  "bk.newType": "Nieuwe soort afspraak",
  "bk.minutes": "{n} min",
  "bk.inactive": "Uit",
  "bk.loc.video": "Videogesprek",
  "bk.loc.place": "Ter plaatse",
  "bk.loc.phone": "Telefoongesprek",
  "bk.loc.none": "Geen locatie",
  "bk.name": "Naam",
  "bk.typeAddress": "Linkadres",
  "bk.slugUsed": "Een andere soort afspraak gebruikt dit adres.",
  "bk.duration": "Duur",
  "bk.interval": "Begintijden",
  "bk.every": "elke {n} min",
  "bk.color": "Kleur",
  "bk.destination": "Toevoegen aan agenda",
  "bk.schedule": "Tijden",
  "bk.location": "Locatie",
  "bk.noVideo": "Videogesprekken worden alleen toegevoegd voor Google- en Microsoft-agenda's.",
  "bk.bufferBefore": "Marge vooraf",
  "bk.bufferAfter": "Marge achteraf",
  "bk.notice": "Minimale termijn",
  "bk.horizon": "Boekbaar tot",
  "bk.dailyLimit": "Limiet per dag",
  "bk.noLimit": "Geen limiet",
  "bk.inMinutes": "minuten",
  "bk.inHours": "uur",
  "bk.inDays": "dagen vooruit",
  "bk.questions": "Vragen",
  "bk.questionsHint": "Naam en e-mail worden altijd gevraagd. Voeg hier uw eigen vragen toe.",
  "bk.questionLabel": "Vraag",
  "bk.questionType": "Soort antwoord",
  "bk.q.text": "Korte tekst",
  "bk.q.textarea": "Lange tekst",
  "bk.q.choice": "Keuze",
  "bk.q.checkbox": "Selectievakje",
  "bk.required": "Verplicht",
  "bk.removeQuestion": "Vraag verwijderen",
  "bk.optionsHint": "Keuzes, gescheiden door komma's",
  "bk.addQuestion": "Vraag toevoegen",
  "bk.active": "Boekbaar",
  "bk.deleteConfirm": "Deze soort afspraak verwijderen? Bestaande boekingen blijven in uw agenda.",
  "bk.schedules": "Tijden",
  "bk.scheduleName": "Naam",
  "bk.closed": "Gesloten",
  "bk.addRange": "Tijden toevoegen",
  "bk.removeRange": "Tijden verwijderen",
  "bk.overrides": "Uitzonderingen per datum",
  "bk.overridesHint": "Sluit een dag of wijzig de tijden, bijvoorbeeld voor vakantie.",
  "bk.overrideDate": "Datum",
  "bk.addOverride": "Datum toevoegen",
  "bk.removeOverride": "Datum verwijderen",
  "bk.bookings": "Boekingen",
  "bk.upcoming": "Komend",
  "bk.past": "Voorbij",
  "bk.noBookings": "Nog geen boekingen.",
  "bk.cancelled": "Geannuleerd",
  "bk.cancelBooking": "Boeking annuleren",
  "bk.cancelConfirm": "De persoon krijgt bericht.",
  "pub.bookWith": "Een moment boeken met",
  "pub.noTypes": "Er zijn momenteel geen soorten afspraken beschikbaar.",
  "pub.notFound": "Deze pagina bestaat niet",
  "pub.notFoundBody": "Controleer de link die u hebt ontvangen.",
  "pub.loc.video": "Videogesprek (link per e-mail)",
  "pub.loc.phone": "Telefoongesprek",
  "pub.loc.none": "Details per e-mail",
  "pub.timezone": "Tijdzone",
  "pub.noSlots": "Geen vrije tijden deze maand. Probeer de volgende.",
  "pub.otherTime": "Ander moment kiezen",
  "pub.phone": "Telefoonnummer",
  "pub.confirm": "Boeking bevestigen",
  "pub.confirmed": "Geboekt",
  "pub.confirmedBody": "Er is een bevestiging naar {email} gestuurd, met de agenda-uitnodiging.",
  "pub.manage": "Wijzigen of annuleren",
  "pub.isCancelled": "Deze afspraak is geannuleerd.",
  "pub.reschedule": "Ander moment kiezen",
  "pub.cancel": "Afspraak annuleren",
  "pub.cancelConfirm": "Deze afspraak annuleren?",
  "pub.cancelledOk": "De afspraak is geannuleerd.",
  "pub.movedOk": "De afspraak is verplaatst.",
  "pub.bookAgain": "Opnieuw boeken",
  "error.SLUG_TAKEN": "Dit adres is al in gebruik. Kies een ander.",
  "error.SLOT_TAKEN": "Dit moment is net geboekt. Kies een ander.",
  "settings.sharing": "Delen",
  "share.busy": "Bezet",
  "share.withMe": "Gedeeld met mij",
  "share.manage": "Delen beheren",
  "share.intro": "Deel uw gecombineerde agenda met anderen. Kies per persoon en per agenda wat ze zien. Wijzigingen gelden meteen.",
  "share.invite": "Mijn agenda delen",
  "share.sent": "{email} is uitgenodigd.",
  "share.send": "Delen",
  "share.help.busy": "ze zien de tijden, gemarkeerd als ‘Bezet’. Verder verlaat niets de server.",
  "share.help.details": "ze zien ook de titel, locatie, beschrijving en gasten.",
  "share.help.hidden": "de agenda verschijnt helemaal niet.",
  "share.mine": "Mijn gedeelde agenda's",
  "share.mineEmpty": "U hebt uw agenda nog niet gedeeld.",
  "share.withMeEmpty": "Nog niemand heeft een agenda met u gedeeld.",
  "share.pending": "Wacht tot het account is aangemaakt",
  "share.active": "Actief",
  "share.visibility": "Zichtbaarheid",
  "share.privateNote": "Afspraken die bij Google, Microsoft of op de CalDAV-server als privé zijn gemarkeerd, verschijnen altijd als bezet. Een later toegevoegde agenda krijgt zijn standaardzichtbaarheid.",
  "share.revokeConfirm": "Stoppen met delen met {name}? De toegang verdwijnt meteen.",
  "share.revoke": "Stoppen met delen",
  "share.label": "Weergegeven naam",
  "share.show": "Tonen",
  "error.SHARE_EXISTS": "Uw agenda wordt al met deze persoon gedeeld.",
  "ev.new": "Nieuwe afspraak",
  "ev.edit": "Bewerken",
  "ev.delete": "Verwijderen",
  "ev.create": "Aanmaken",
  "ev.saving": "Opslaan…",
  "ev.saved": "Opgeslagen",
  "ev.deleted": "Afspraak verwijderd",
  "ev.moveFailed": "De afspraak kon niet worden verplaatst.",
  "ev.cannotEdit": "Deze agenda is alleen-lezen: de afspraken kunnen hier niet worden gewijzigd.",
  "ev.titlePlaceholder": "Titel toevoegen",
  "ev.starts": "Begint",
  "ev.ends": "Eindigt",
  "ev.endBeforeStart": "Het einde moet na het begin liggen.",
  "ev.conflict": "Overlapt met {list}.",
  "ev.repeat": "Herhalen",
  "ev.repeat.none": "Herhaalt niet",
  "ev.repeat.daily": "Elke dag",
  "ev.repeat.weekly": "Elke week",
  "ev.repeat.monthly": "Elke maand",
  "ev.repeat.yearly": "Elk jaar",
  "ev.repeat.every": "Elke",
  "ev.repeat.unit.daily": "dag(en)",
  "ev.repeat.unit.weekly": "week/weken",
  "ev.repeat.unit.monthly": "maand(en)",
  "ev.repeat.unit.yearly": "jaar",
  "ev.repeat.on": "Op",
  "ev.repeat.ends": "Eindigt",
  "ev.repeat.never": "Nooit",
  "ev.repeat.onDate": "Op een datum",
  "ev.repeat.after": "Na",
  "ev.repeat.times": "keer",
  "ev.repeat.locked": "Deze afspraak herhaalt zich. Kies hieronder ‘Deze en volgende afspraken’ of ‘Alle afspraken in de reeks’ om de herhaling te wijzigen.",
  "ev.placePlaceholder": "Plaats of adres toevoegen",
  "ev.placeAttribution": "Plaatsen © OpenStreetMap-bijdragers",
  "ev.meet": "Google Meet-videogesprek toevoegen",
  "ev.teams": "Microsoft Teams-vergadering toevoegen",
  "ev.videoLink": "Videolink",
  "ev.guestsPlaceholder": "Gasten toevoegen via e-mail",
  "ev.removeGuest": "{email} verwijderen",
  "ev.invitesNote": "Gasten krijgen de uitnodiging van uw agendadienst.",
  "ev.availability": "Weergeven als",
  "ev.busy": "Bezet",
  "ev.freeOption": "Vrij",
  "ev.reminder": "Herinnering",
  "ev.reminder.default": "Standaard van de agenda",
  "ev.reminder.atStart": "Bij het begin",
  "ev.reminder.minutes": "{n} minuten vooraf",
  "ev.reminder.hour": "1 uur vooraf",
  "ev.reminder.2hours": "2 uur vooraf",
  "ev.reminder.day": "1 dag vooraf",
  "ev.privateToggle": "Privé",
  "ev.privateHint": "mensen met wie u uw agenda deelt, zien alleen dat u bezet bent.",
  "ev.description": "Beschrijving",
  "ev.scope": "Toepassen op",
  "ev.scope.this": "Deze afspraak",
  "ev.scope.all": "Alle afspraken in de reeks",
  "ev.confirmDelete": "Deze afspraak verwijderen?",
  "ev.deleteNotifies": "Gasten krijgen bericht dat de afspraak is geannuleerd.",
  "error.READ_ONLY": "Deze agenda is alleen-lezen.",
  "error.EVENT_REJECTED": "De agendadienst heeft de wijziging geweigerd.",
  "error.PROVIDER_CONFLICT": "De afspraak is elders gewijzigd. Sluit hem en probeer het opnieuw.",
  "error.PROVIDER_NOT_CONFIGURED": "Deze koppeling is nog niet ingesteld op deze instantie.",
  "error.PROVIDER_AUTH": "De server weigerde de gebruikersnaam of het wachtwoord.",
  "error.PROVIDER_UNREACHABLE": "De server is niet bereikbaar. Controleer het adres en probeer opnieuw.",
  "error.PROVIDER_INVALID": "Op dit adres is geen bruikbare agenda gevonden. Controleer het en probeer opnieuw.",
};

const de: Dict = {
  "lang.name": "Deutsch",
  "common.language": "Sprache",
  "common.email": "E-Mail",
  "common.password": "Passwort",
  "common.name": "Name",
  "common.save": "Speichern",
  "common.saved": "Gespeichert",
  "common.cancel": "Abbrechen",
  "common.continue": "Weiter",
  "common.back": "Zurück",
  "common.loading": "Wird geladen…",
  "common.passwordHint": "Mindestens 10 Zeichen.",
  "common.backToLogin": "Zurück zur Anmeldung",
  "login.title": "Anmelden",
  "login.remember": "Angemeldet bleiben",
  "login.submit": "Anmelden",
  "login.forgot": "Passwort vergessen?",
  "login.noAccount": "Noch kein Konto?",
  "login.create": "Konto erstellen",
  "signup.title": "Erstelle dein Konto",
  "signup.invited": "Du wurdest mit {email} eingeladen.",
  "signup.submit": "Konto erstellen",
  "signup.haveAccount": "Du hast schon ein Konto?",
  "signup.closed": "Registrierungen sind auf dieser Instanz geschlossen.",
  "signup.inviteOnly": "Diese Instanz funktioniert nur mit Einladung. Bitte den Administrator um einen Einladungslink.",
  "signup.checkTitle": "Sieh in dein Postfach",
  "signup.checkBody": "Wir haben einen Bestätigungslink an {email} geschickt. Er ist 24 Stunden gültig.",
  "signup.resend": "Link erneut senden",
  "signup.resent": "Wenn die Adresse auf Bestätigung wartet, ist ein neuer Link unterwegs.",
  "verify.title": "Deine Adresse wird bestätigt",
  "verify.working": "Einen Moment…",
  "forgot.title": "Passwort zurücksetzen",
  "forgot.body": "Gib deine E-Mail-Adresse ein. Wenn ein Konto existiert, bekommst du einen Link, der 1 Stunde gültig ist.",
  "forgot.submit": "Link senden",
  "forgot.sent": "Wenn für diese Adresse ein Konto existiert, ist ein Link unterwegs.",
  "reset.title": "Wähle ein neues Passwort",
  "reset.submit": "Passwort speichern",
  "setup.title": "Maki Calendar einrichten",
  "setup.intro": "Drei Schritte: dein Administratorkonto, der E-Mail-Versand und wer sich registrieren darf.",
  "setup.step.admin": "Administrator",
  "setup.step.mail": "E-Mails",
  "setup.step.access": "Registrierung",
  "setup.brevoKey": "Brevo-API-Schlüssel",
  "setup.brevoHelp": "Öffne in Brevo SMTP & API und dann API-Schlüssel. Authentifiziere auch deine Absenderdomain (SPF und DKIM), damit E-Mails nicht im Spam landen.",
  "setup.fromEmail": "Absenderadresse",
  "setup.fromName": "Absendername",
  "setup.timezone": "Standardzeitzone",
  "setup.finish": "Einrichtung abschließen",
  "setup.noToken": "Dieser Einrichtungslink ist unvollständig. Öffne den vollständigen Link, den das Installationsskript anzeigt.",
  "setup.done": "Die Einrichtung ist abgeschlossen.",
  "mode.label": "Wer darf ein Konto erstellen?",
  "mode.invite": "Nur eingeladene Personen",
  "mode.invite.help": "Du lädst Personen per E-Mail ein. Empfohlen.",
  "mode.open": "Alle",
  "mode.open.help": "Jeder, der die Adresse kennt, kann sich registrieren.",
  "mode.closed": "Niemand",
  "mode.closed.help": "Keine neuen Konten, auch nicht mit Einladung.",
  "nav.calendar": "Kalender",
  "nav.settings": "Einstellungen",
  "nav.logout": "Abmelden",
  "home.hello": "Hallo, {name}",
  "home.emptyTitle": "Verbinde deinen ersten Kalender",
  "home.emptyBody": "Dein Konto ist bereit. Kalender von Google, Microsoft 365, CalDAV und iCal lassen sich ab der nächsten Version verbinden.",
  "home.soon": "Nächste Version",
  "settings.title": "Einstellungen",
  "settings.account": "Mein Konto",
  "settings.security": "Sicherheit",
  "settings.instance": "Instanz",
  "settings.users": "Benutzer",
  "settings.adminOnly": "Administrator",
  "profile.timezone": "Zeitzone",
  "profile.weekStart": "Erster Tag der Woche",
  "profile.home": "Standardstadt",
  "profile.homePlaceholder": "Zum Beispiel Brüssel",
  "profile.homeHint": "Ortsvorschläge werden nach Ihrem aktuellen Standort sortiert, oder nach dieser Stadt, wenn er unbekannt ist.",
  "day.1": "Montag",
  "day.0": "Sonntag",
  "day.6": "Samstag",
  "password.current": "Aktuelles Passwort",
  "password.new": "Neues Passwort",
  "password.change": "Passwort ändern",
  "password.changed": "Passwort geändert. Deine anderen Sitzungen wurden abgemeldet.",
  "instance.mail": "E-Mails (Brevo)",
  "instance.keySaved": "Ein Schlüssel ist gespeichert. Leer lassen, um ihn zu behalten.",
  "instance.test": "Test-E-Mail senden",
  "instance.testSent": "Test-E-Mail an {email} gesendet.",
  "users.invite": "Jemanden einladen",
  "users.inviteSubmit": "Einladung senden",
  "users.invited": "Einladung an {email} gesendet.",
  "users.disable": "Deaktivieren",
  "users.enable": "Aktivieren",
  "users.delete": "Löschen",
  "users.confirmDelete": "Das Konto von {name} löschen? Das lässt sich nicht rückgängig machen.",
  "users.you": "du",
  "users.admin": "Administrator",
  "users.unverified": "E-Mail nicht bestätigt",
  "users.disabled": "deaktiviert",
  "error.INVALID_CREDENTIALS": "E-Mail oder Passwort ist falsch.",
  "error.EMAIL_NOT_VERIFIED": "Bestätige zuerst deine E-Mail-Adresse über den Link, den du bekommen hast.",
  "error.ACCOUNT_DISABLED": "Dieses Konto wurde vom Administrator deaktiviert.",
  "error.EMAIL_TAKEN": "Mit dieser Adresse gibt es bereits ein Konto.",
  "error.REGISTRATION_CLOSED": "Registrierungen sind auf dieser Instanz geschlossen.",
  "error.INVITATION_REQUIRED": "Verwende die E-Mail-Adresse, an die die Einladung ging.",
  "error.INVALID_TOKEN": "Dieser Link ist nicht mehr gültig. Fordere einen neuen an.",
  "error.SETUP_DONE": "Die Einrichtung ist bereits abgeschlossen. Melde dich an.",
  "error.SETUP_TOKEN_INVALID": "Der Einrichtungslink ist falsch. Verwende den Link, den das Skript anzeigt.",
  "error.SETUP_TOKEN_MISSING": "SETUP_TOKEN fehlt in der .env-Datei des Servers.",
  "error.MAIL_NOT_CONFIGURED": "Der E-Mail-Versand ist noch nicht eingerichtet. Der Administrator kann das in den Einstellungen tun.",
  "error.MAIL_FAILED": "Die E-Mail konnte nicht gesendet werden. Prüfe den Brevo-Schlüssel und die Absenderadresse.",
  "error.RATE_LIMITED": "Zu viele Versuche. Warte eine Minute und versuche es erneut.",
  "error.VALIDATION": "Einige Felder müssen korrigiert werden.",
  "error.NETWORK": "Der Server ist nicht erreichbar. Prüfe deine Verbindung.",
  "error.UNAUTHENTICATED": "Deine Sitzung ist abgelaufen. Melde dich erneut an.",
  "error.FORBIDDEN": "Nur der Administrator kann das tun.",
  "error.CSRF": "Die Anfrage wurde abgelehnt. Lade die Seite neu und versuche es erneut.",
  "error.INTERNAL": "Auf dem Server ist ein Fehler aufgetreten. Versuche es gleich noch einmal.",
  "error.field": "Prüfe dieses Feld.",
  "settings.calendars": "Kalender",
  "cal.lead": "Füge beliebig viele Konten hinzu. Wähle für jeden Kalender die Farbe, ob er angezeigt wird, ob er deine Buchungsseite blockiert und was Personen, mit denen du teilst, standardmäßig sehen.",
  "cal.add": "Konto hinzufügen",
  "cal.google.sub": "Gmail oder Google Workspace",
  "cal.microsoft.sub": "Outlook, beruflich oder privat",
  "cal.caldav.sub": "Nextcloud, iCloud, Fastmail…",
  "cal.ics": "iCal-Link",
  "cal.ics.sub": "Abo, nur lesen",
  "cal.notConfigured": "Der Administrator muss zuerst die {provider}-App unter Einstellungen, Instanz einrichten.",
  "cal.continueWith": "Weiter mit {provider}",
  "cal.redirectNote": "Du wirst zu {provider} weitergeleitet, um Maki Calendar das Lesen und Bearbeiten deines Kalenders zu erlauben. Es wird kein Zugriff auf deine E-Mails angefordert.",
  "cal.service": "Dienst",
  "cal.serverUrl": "Serveradresse",
  "cal.username": "Benutzername",
  "cal.appPassword": "App-Passwort",
  "cal.appPasswordHint": "Verwende ein App-Passwort statt deines Hauptpassworts. Für iCloud ist es Pflicht.",
  "cal.connect": "Verbinden und Kalender suchen",
  "cal.icsUrl": "Kalender-Link",
  "cal.icsName": "Anzeigename",
  "cal.subscribe": "Abonnieren",
  "cal.other": "Anderer Server",
  "cal.status.ok": "Synchronisiert {when}",
  "cal.status.never": "Warte auf die erste Synchronisierung",
  "cal.status.syncing": "Wird synchronisiert…",
  "cal.status.error": "Synchronisierungsproblem: {error}",
  "cal.status.reauth": "Der Zugriff wurde verweigert. Verbinde das Konto neu.",
  "cal.sync": "Jetzt synchronisieren",
  "cal.reconnect": "Neu verbinden",
  "cal.remove": "Entfernen",
  "cal.removeConfirm": "{label} entfernen? Die Kalender verschwinden aus Maki Calendar. Beim Anbieter wird nichts gelöscht.",
  "cal.removeOk": "Konto entfernen",
  "cal.keep": "Behalten",
  "cal.show": "Anzeigen",
  "cal.blocks": "Blockiert Buchungen",
  "cal.shareDefault": "Geteilt als",
  "cal.default": "Standard",
  "cal.readOnly": "nur lesen",
  "cal.color": "Farbe von {name}",
  "cal.connected": "{provider}-Konto verbunden. Die Kalender werden synchronisiert.",
  "cal.added": "Konto hinzugefügt. Die Kalender werden synchronisiert.",
  "cal.empty": "Noch kein Konto verbunden.",
  "vis.hidden": "Verborgen",
  "vis.busy": "Beschäftigt",
  "vis.details": "Details",
  "oauth.not_configured": "Diese Verbindung ist noch nicht eingerichtet. Der Administrator muss die App unter Einstellungen, Instanz konfigurieren.",
  "oauth.cancelled": "Verbindung abgebrochen.",
  "oauth.expired": "Die Verbindung hat zu lange gedauert. Versuche es erneut.",
  "oauth.failed": "Die Verbindung ist fehlgeschlagen. Versuche es erneut.",
  "oauth.admin_consent": "Deine Organisation verlangt, dass ein Administrator Maki Calendar genehmigt. Schicke diesen Link an deinen Microsoft-365-Administrator:",
  "instance.providers": "Kalender-Apps (OAuth)",
  "instance.providersHelp": "Erstelle die Apps bei Google und Microsoft und füge hier ihre Kennungen ein. Jede App braucht die unten angezeigte Weiterleitungs-URI.",
  "instance.clientId": "Client ID",
  "instance.clientSecret": "Client-Geheimnis",
  "instance.secretSaved": "Ein Geheimnis ist gespeichert. Leer lassen, um es zu behalten.",
  "instance.redirectUri": "Weiterleitungs-URI",
  "home.upcoming": "Nächste 14 Tage",
  "home.nothing": "Nichts geplant.",
  "home.manage": "Kalender verwalten",
  "home.addFirst": "Kalender hinzufügen",
  "home.attention": "Ein Konto braucht deine Aufmerksamkeit.",
  "home.connectBody": "Verbinde Google, Microsoft 365, CalDAV (Nextcloud, iCloud…) oder einen iCal-Link, um alles an einem Ort zu sehen.",
  "common.allDay": "Ganztägig",
  "common.join": "Beitreten",
  "common.today": "Heute",
  "common.tomorrow": "Morgen",
  "view.week": "Woche",
  "view.month": "Monat",
  "view.agenda": "Agenda",
  "view.boxes": "Kästen",
  "view.label": "Ansicht",
  "cal.prev": "Zurück",
  "cal.next": "Weiter",
  "cal.weekNumber": "KW {n}",
  "cal.calendars": "Kalender",
  "cal.toggleSidebar": "Kalender und Minikalender",
  "cal.more": "+{n} weitere",
  "cal.earlier": "Frühere Tage anzeigen",
  "cal.later": "Spätere Tage anzeigen",
  "cal.windowEnd": "Kalender werden bis zu 18 Monate im Voraus synchronisiert.",
  "cal.windowStart": "Kalender werden ab 6 Monaten rückwirkend synchronisiert.",
  "cal.noTitle": "(Ohne Titel)",
  "cal.shortcuts": "Tastenkürzel: N neuer Termin, T heute, J/K oder ← → zurück und weiter, D 3 W M L B für die Ansicht, / suchen.",
  "ev.free": "Als frei angezeigt",
  "ev.tentative": "Vorläufig",
  "ev.private": "Privat",
  "ev.recurring": "Wiederholt sich",
  "ev.declined": "Abgelehnt",
  "ev.guests": "Gäste",
  "ev.organizer": "Organisator",
  "ev.status.accepted": "Zugesagt",
  "ev.status.declined": "Abgelehnt",
  "ev.status.tentative": "Vielleicht",
  "ev.status.needs-action": "Antwort ausstehend",
  "ev.openMap": "In einer Karte öffnen",
  "ev.close": "Schließen",
  "ev.notes": "Notizen",
  "ev.location": "Ort",
  "cal.calendar": "Kalender",
  "guide.google": "So erstellen Sie die Google-App",
  "guide.google.1": "Öffnen Sie console.cloud.google.com und legen Sie ein Projekt an (zum Beispiel „Maki Calendar“).",
  "guide.google.2": "Aktivieren Sie unter APIs & Dienste, Bibliothek die „Google Calendar API“.",
  "guide.google.3": "Öffnen Sie Google Auth Platform (OAuth-Zustimmungsbildschirm), Erste Schritte: App-Name, Support-E-Mail, Zielgruppe „Extern“, Kontakt-E-Mail.",
  "guide.google.4": "Unter Branding: Startseite {home}, Datenschutzerklärung {privacy}, Nutzungsbedingungen {terms}, autorisierte Domain {domain}.",
  "guide.google.5": "Fügen Sie unter Datenzugriff die Bereiche openid, email, profile und https://www.googleapis.com/auth/calendar hinzu.",
  "guide.google.6": "Unter Clients, Client erstellen, Typ „Webanwendung“, autorisierte Weiterleitungs-URI: die oben angezeigte Adresse.",
  "guide.google.7": "Fügen Sie Client-ID und Secret hier ein und speichern Sie.",
  "guide.google.8": "Unter Zielgruppe: App veröffentlichen. Um die Warnung „nicht verifizierte App“ zu entfernen, beantragen Sie die Überprüfung im Verifizierungscenter: Dafür dienen die Seiten oben und ein kurzes Demovideo.",
  "guide.microsoft": "So erstellen Sie die Microsoft-App",
  "guide.microsoft.1": "Melden Sie sich bei entra.microsoft.com mit einem Konto an, das ein eigenes Verzeichnis hat (ein kostenloses Azure-Konto legt eines an).",
  "guide.microsoft.2": "App-Registrierungen, Neue Registrierung. Unterstützte Konten: „Konten in allen Organisationsverzeichnissen und persönliche Microsoft-Konten“. Umleitungs-URI, Typ Web: die oben angezeigte Adresse.",
  "guide.microsoft.3": "Kopieren Sie die Anwendungs-ID (Client).",
  "guide.microsoft.4": "Zertifikate & Geheimnisse, Neuer geheimer Clientschlüssel (24 Monate). Kopieren Sie den Wert, nicht die geheime ID, und notieren Sie das Ablaufdatum.",
  "guide.microsoft.5": "API-Berechtigungen, Hinzufügen, Microsoft Graph, Delegiert: openid, profile, email, offline_access, User.Read, Calendars.ReadWrite. Keine Administratorzustimmung erteilen.",
  "guide.microsoft.6": "Fügen Sie ID und Geheimnis hier ein und speichern Sie.",
  "guide.microsoft.7": "Optional: Um „Administratorgenehmigung erforderlich“ in manchen Organisationen zu vermeiden, werden Sie verifizierter Herausgeber (Branding & Eigenschaften) mit einer Microsoft-Cloud-Partner-Program-ID und einer verifizierten Domain.",
  "legal.privacy": "Datenschutzerklärung",
  "legal.terms": "Nutzungsbedingungen",
  "instance.legal": "Rechtliche Seiten",
  "instance.legalHelp": "Google verlangt diese Seiten zur Überprüfung Ihrer App. Sie werden aus dem erstellt, was Maki Calendar tatsächlich tut; Sie können sie ersetzen.",
  "instance.legalOperator": "Betreiber (Person oder Firma)",
  "instance.legalContact": "Kontakt-E-Mail",
  "instance.legalEdit": "Text bearbeiten",
  "instance.legalGenerated": "Generierter Text in Verwendung.",
  "instance.legalReset": "Zum generierten Text zurückkehren",
  "settings.status": "Status",
  "status.version": "Version",
  "status.since": "läuft seit {when}",
  "status.database": "Datenbank",
  "status.mail": "E-Mail-Versand",
  "status.push": "Sofortige Synchronisierung",
  "status.pushOff": "Aus: Die Adresse muss öffentlich und HTTPS sein.",
  "status.users": "Nutzer",
  "status.connections": "Verbundene Konten",
  "status.failing": "Konten mit Problemen",
  "status.noFailing": "Kein Konto hat Probleme.",
  "status.backups": "Sicherungen",
  "status.noBackups": "Noch keine Sicherung aufgeführt. Sie laufen jede Nacht um 3:15 Uhr.",
  "status.restore": "Wiederherstellen: sudo ./scripts/restore.sh /var/backups/confluence/<Datei>",
  "status.errors": "Aktuelle Fehler",
  "status.noErrors": "Keine aktuellen Fehler.",
  "status.ok": "OK",
  "status.problem": "Problem",
  "bk.busyCalendars": "Diese Kalender blockieren Ihre Buchungsseite: {list}. Ändern Sie „Zählt als beschäftigt“ unter Kalender.",
  "bk.busyNone": "Kein Kalender zählt als beschäftigt: Alle Zeiten in Ihren Zeiten werden angeboten. Aktivieren Sie „Zählt als beschäftigt“ unter Kalender.",
  "ev.scope.following": "Dieser und alle folgenden Termine",
  "ev.repeat.custom": "Diese Serie nutzt eine Regel, die das Formular nicht darstellen kann (zum Beispiel „der 2. Dienstag im Monat“). Ändern Sie sie in Google, Microsoft oder Ihrer CalDAV-App.",
  "instance.places": "Ortssuche",
  "instance.placesHelp": "Standardmäßig stammen Orte von OpenStreetMap (kostenlos, ohne Schlüssel). Mit einem Google-Places-API-Schlüssel kommen die Vorschläge von Google, das mehr Geschäfte und Restaurants kennt. Google berechnet Suchen über das kostenlose Monatskontingent hinaus.",
  "instance.placesKey": "Google-Places-API-Schlüssel",
  "instance.placesSaved": "Ein Schlüssel ist gespeichert. Leer lassen, um ihn zu behalten.",
  "instance.placesNone": "Kein Schlüssel: OpenStreetMap wird verwendet.",
  "instance.placesRemove": "Schlüssel entfernen und zu OpenStreetMap zurückkehren",
  "settings.notifications": "Benachrichtigungen",
  "notify.intro": "Wählen Sie, welche E-Mails Maki Calendar Ihnen sendet. Besucher erhalten immer die Bestätigung ihrer Buchung.",
  "notify.bookings": "Buchungen",
  "notify.bookings.hint": "Eine E-Mail bei jeder neuen Buchung, Absage oder Verschiebung auf Ihrer Buchungsseite.",
  "notify.shares": "Neue Freigaben",
  "notify.shares.hint": "Eine E-Mail, wenn jemand einen Kalender mit Ihnen teilt.",
  "notify.reconnect": "Neu zu verbindende Konten",
  "notify.reconnect.hint": "Eine E-Mail, wenn ein Konto seit über 24 Stunden nicht synchronisiert wurde.",
  "notify.dailyRecap": "Übersicht für morgen",
  "notify.dailyRecap.hint": "Jeden Abend um 18 Uhr die Liste Ihrer Termine für den nächsten Tag.",
  "view.day": "Tag",
  "view.threeDays": "3 Tage",
  "search.placeholder": "Suchen",
  "search.none": "Kein Termin gefunden.",
  "cal.toggleStrip": "Mehr Wochen anzeigen",
  "settings.display": "Anzeige",
  "display.startView": "Ansicht beim Start",
  "display.startAuto": "Woche (Kästen auf dem Telefon)",
  "display.theme": "Design",
  "display.theme.system": "Wie das Gerät",
  "display.theme.light": "Hell",
  "display.theme.dark": "Dunkel",
  "display.weekNumbers": "Kalenderwochen anzeigen",
  "display.showBlocks": "Blöcke der Kreuzblockierung (blass) anzeigen statt ausblenden",
  "profile.timeFormat": "Zeitformat",
  "profile.timeAuto": "Wie die Sprache",
  "data.title": "Meine Daten",
  "data.exportBody": "Laden Sie alles herunter, was Maki Calendar über Sie speichert, als JSON-Datei. Passwörter und Zugangsschlüssel zu Konten sind nicht enthalten.",
  "data.export": "Meine Daten herunterladen",
  "data.delete": "Mein Konto löschen",
  "data.deleteWarning": "Ihr Konto, Ihre Verbindungen, Freigaben, Buchungsseite und Buchungen werden endgültig gelöscht. Bei Google, Microsoft oder auf Ihrem CalDAV-Server wird nichts gelöscht; der Zugriff von Maki Calendar wird widerrufen.",
  "sessions.title": "Offene Sitzungen",
  "sessions.current": "Dieses Gerät",
  "sessions.lastSeen": "aktiv {when}",
  "sessions.revoke": "Abmelden",
  "sessions.revokeOthers": "Überall sonst abmelden",
  "totp.title": "Zwei-Faktor-Authentifizierung",
  "totp.intro": "Nach dem Passwort fragt Maki Calendar nach einem Code aus einer Authenticator-App (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).",
  "totp.setup": "Zwei-Faktor-Authentifizierung aktivieren",
  "totp.step1": "Scannen Sie diesen QR-Code mit Ihrer Authenticator-App.",
  "totp.step2": "Geben Sie den 6-stelligen Code ein, den sie anzeigt.",
  "totp.manual": "Scannen nicht möglich? Geben Sie diesen Schlüssel ein:",
  "totp.code": "Code",
  "totp.enable": "Aktivieren",
  "totp.on": "Die Zwei-Faktor-Authentifizierung ist aktiv",
  "totp.codesLeft": "noch {n} Backup-Codes",
  "totp.newCodes": "Neue Backup-Codes",
  "totp.disable": "Deaktivieren",
  "totp.saveCodes": "Bewahren Sie diese Backup-Codes sicher auf. Jeder erlaubt eine Anmeldung, falls Sie Ihr Telefon verlieren. Sie werden nicht erneut angezeigt.",
  "totp.copyCodes": "Codes kopieren",
  "totp.loginBody": "Geben Sie den Code aus Ihrer Authenticator-App ein.",
  "totp.backupHint": "Sie können auch einen Ihrer Backup-Codes eingeben.",
  "settings.blocking": "Kreuzblockierung",
  "blc.intro": "Halten Sie Ihre Kalender im Gleichklang: Jeder belegte Termin in den Quellkalendern erzeugt einen privaten Block „Beschäftigt“ im Zielkalender. Kolleginnen und Kollegen, die nur diesen Kalender sehen, wissen dann, dass Sie nicht frei sind. Blöcke folgen ihrer Quelle, wenn sie sich verschiebt oder verschwindet.",
  "blc.new": "Neue Regel",
  "blc.edit": "Regel bearbeiten",
  "blc.none": "Noch keine Regel.",
  "blc.count": "{n} Blöcke",
  "blc.lastRun": "aktualisiert {when}",
  "blc.sources": "Quellkalender",
  "blc.sourcesHint": "Ihre belegten Termine (nicht frei, nicht abgelehnt) erzeugen Blöcke.",
  "blc.pickSource": "Wählen Sie mindestens einen Quellkalender.",
  "blc.target": "Zielkalender",
  "blc.targetHint": "Hier entstehen die Blöcke, privat und belegt, ohne Gäste und ohne Beschreibung.",
  "blc.title": "Titel der Blöcke",
  "blc.titleHint": "Zum Beispiel Beschäftigt.",
  "blc.window": "Blockieren für",
  "blc.allDay": "Ganztägige Termine einbeziehen",
  "blc.active": "Regel aktiv",
  "blc.deleteConfirm": "Diese Regel löschen?",
  "blc.removeBlocks": "Auch die {n} bereits erstellten Blöcke löschen",
  "share.resend": "Einladung erneut senden",
  "share.resent": "Einladung erneut gesendet",
  "settings.booking": "Buchung",
  "bk.intro": "Lassen Sie andere einen Termin bei Ihnen buchen. Angeboten werden nur freie Zeiten in allen Kalendern, die als beschäftigt zählen.",
  "bk.page": "Buchungsseite",
  "bk.address": "Seitenadresse",
  "bk.copyLink": "Link kopieren",
  "bk.copied": "Kopiert",
  "bk.slugFirst": "Wählen Sie zuerst die Adresse Ihrer Seite.",
  "bk.types": "Terminarten",
  "bk.newType": "Neue Terminart",
  "bk.minutes": "{n} Min.",
  "bk.inactive": "Aus",
  "bk.loc.video": "Videoanruf",
  "bk.loc.place": "Vor Ort",
  "bk.loc.phone": "Telefonat",
  "bk.loc.none": "Ohne Ort",
  "bk.name": "Name",
  "bk.typeAddress": "Linkadresse",
  "bk.slugUsed": "Eine andere Terminart nutzt diese Adresse.",
  "bk.duration": "Dauer",
  "bk.interval": "Startzeiten",
  "bk.every": "alle {n} Min.",
  "bk.color": "Farbe",
  "bk.destination": "In Kalender eintragen",
  "bk.schedule": "Zeiten",
  "bk.location": "Ort",
  "bk.noVideo": "Videoanrufe werden nur für Google- und Microsoft-Kalender hinzugefügt.",
  "bk.bufferBefore": "Puffer davor",
  "bk.bufferAfter": "Puffer danach",
  "bk.notice": "Mindestvorlauf",
  "bk.horizon": "Buchbar bis",
  "bk.dailyLimit": "Limit pro Tag",
  "bk.noLimit": "Kein Limit",
  "bk.inMinutes": "Minuten",
  "bk.inHours": "Stunden",
  "bk.inDays": "Tage im Voraus",
  "bk.questions": "Fragen",
  "bk.questionsHint": "Name und E-Mail werden immer abgefragt. Fügen Sie hier eigene Fragen hinzu.",
  "bk.questionLabel": "Frage",
  "bk.questionType": "Antwortart",
  "bk.q.text": "Kurzer Text",
  "bk.q.textarea": "Langer Text",
  "bk.q.choice": "Auswahl",
  "bk.q.checkbox": "Kontrollkästchen",
  "bk.required": "Pflichtfeld",
  "bk.removeQuestion": "Frage entfernen",
  "bk.optionsHint": "Auswahlmöglichkeiten, durch Kommas getrennt",
  "bk.addQuestion": "Frage hinzufügen",
  "bk.active": "Buchbar",
  "bk.deleteConfirm": "Diese Terminart löschen? Bestehende Buchungen bleiben in Ihrem Kalender.",
  "bk.schedules": "Zeiten",
  "bk.scheduleName": "Name",
  "bk.closed": "Geschlossen",
  "bk.addRange": "Zeiten hinzufügen",
  "bk.removeRange": "Zeiten entfernen",
  "bk.overrides": "Ausnahmen nach Datum",
  "bk.overridesHint": "Schließen Sie einen Tag oder ändern Sie die Zeiten, etwa für Urlaub.",
  "bk.overrideDate": "Datum",
  "bk.addOverride": "Datum hinzufügen",
  "bk.removeOverride": "Datum entfernen",
  "bk.bookings": "Buchungen",
  "bk.upcoming": "Anstehend",
  "bk.past": "Vergangen",
  "bk.noBookings": "Noch keine Buchungen.",
  "bk.cancelled": "Abgesagt",
  "bk.cancelBooking": "Buchung absagen",
  "bk.cancelConfirm": "Die Person wird informiert.",
  "pub.bookWith": "Einen Termin buchen bei",
  "pub.noTypes": "Derzeit sind keine Terminarten verfügbar.",
  "pub.notFound": "Diese Seite existiert nicht",
  "pub.notFoundBody": "Prüfen Sie den erhaltenen Link.",
  "pub.loc.video": "Videoanruf (Link per E-Mail)",
  "pub.loc.phone": "Telefonat",
  "pub.loc.none": "Details per E-Mail",
  "pub.timezone": "Zeitzone",
  "pub.noSlots": "Keine freien Zeiten in diesem Monat. Versuchen Sie den nächsten.",
  "pub.otherTime": "Andere Zeit wählen",
  "pub.phone": "Telefonnummer",
  "pub.confirm": "Buchung bestätigen",
  "pub.confirmed": "Gebucht",
  "pub.confirmedBody": "Eine Bestätigung mit der Kalendereinladung wurde an {email} gesendet.",
  "pub.manage": "Ändern oder absagen",
  "pub.isCancelled": "Dieser Termin wurde abgesagt.",
  "pub.reschedule": "Andere Zeit wählen",
  "pub.cancel": "Termin absagen",
  "pub.cancelConfirm": "Diesen Termin absagen?",
  "pub.cancelledOk": "Der Termin ist abgesagt.",
  "pub.movedOk": "Der Termin wurde verschoben.",
  "pub.bookAgain": "Erneut buchen",
  "error.SLUG_TAKEN": "Diese Adresse wird bereits verwendet. Wählen Sie eine andere.",
  "error.SLOT_TAKEN": "Diese Zeit wurde gerade gebucht. Wählen Sie eine andere.",
  "settings.sharing": "Freigabe",
  "share.busy": "Beschäftigt",
  "share.withMe": "Mit mir geteilt",
  "share.manage": "Freigaben verwalten",
  "share.intro": "Teilen Sie Ihren zusammengeführten Kalender mit anderen. Wählen Sie für jede Person und jeden Kalender, was sie sieht. Änderungen gelten sofort.",
  "share.invite": "Meinen Kalender teilen",
  "share.sent": "{email} wurde eingeladen.",
  "share.send": "Teilen",
  "share.help.busy": "sie sieht die Zeiten, markiert als „Beschäftigt“. Sonst verlässt nichts den Server.",
  "share.help.details": "sie sieht zusätzlich Titel, Ort, Beschreibung und Gäste.",
  "share.help.hidden": "der Kalender erscheint überhaupt nicht.",
  "share.mine": "Meine Freigaben",
  "share.mineEmpty": "Sie haben Ihren Kalender noch nicht geteilt.",
  "share.withMeEmpty": "Noch niemand hat einen Kalender mit Ihnen geteilt.",
  "share.pending": "Wartet auf die Kontoerstellung",
  "share.active": "Aktiv",
  "share.visibility": "Sichtbarkeit",
  "share.privateNote": "Termine, die bei Google, Microsoft oder auf dem CalDAV-Server als privat markiert sind, erscheinen immer als beschäftigt. Ein später hinzugefügter Kalender nutzt seine Standardsichtbarkeit.",
  "share.revokeConfirm": "Freigabe für {name} beenden? Der Zugriff endet sofort.",
  "share.revoke": "Freigabe beenden",
  "share.label": "Angezeigter Name",
  "share.show": "Anzeigen",
  "error.SHARE_EXISTS": "Ihr Kalender ist bereits mit dieser Person geteilt.",
  "ev.new": "Neuer Termin",
  "ev.edit": "Bearbeiten",
  "ev.delete": "Löschen",
  "ev.create": "Erstellen",
  "ev.saving": "Wird gespeichert…",
  "ev.saved": "Gespeichert",
  "ev.deleted": "Termin gelöscht",
  "ev.moveFailed": "Der Termin konnte nicht verschoben werden.",
  "ev.cannotEdit": "Dieser Kalender ist schreibgeschützt: Seine Termine können hier nicht geändert werden.",
  "ev.titlePlaceholder": "Titel hinzufügen",
  "ev.starts": "Beginn",
  "ev.ends": "Ende",
  "ev.endBeforeStart": "Das Ende muss nach dem Beginn liegen.",
  "ev.conflict": "Überschneidet sich mit {list}.",
  "ev.repeat": "Wiederholen",
  "ev.repeat.none": "Keine Wiederholung",
  "ev.repeat.daily": "Täglich",
  "ev.repeat.weekly": "Wöchentlich",
  "ev.repeat.monthly": "Monatlich",
  "ev.repeat.yearly": "Jährlich",
  "ev.repeat.every": "Alle",
  "ev.repeat.unit.daily": "Tag(e)",
  "ev.repeat.unit.weekly": "Woche(n)",
  "ev.repeat.unit.monthly": "Monat(e)",
  "ev.repeat.unit.yearly": "Jahr(e)",
  "ev.repeat.on": "Am",
  "ev.repeat.ends": "Endet",
  "ev.repeat.never": "Nie",
  "ev.repeat.onDate": "An einem Datum",
  "ev.repeat.after": "Nach",
  "ev.repeat.times": "Mal",
  "ev.repeat.locked": "Dieser Termin wiederholt sich. Um die Wiederholung zu ändern, wählen Sie unten „Dieser und alle folgenden Termine“ oder „Alle Termine der Serie“.",
  "ev.placePlaceholder": "Ort oder Adresse hinzufügen",
  "ev.placeAttribution": "Orte © OpenStreetMap-Mitwirkende",
  "ev.meet": "Google Meet-Videokonferenz hinzufügen",
  "ev.teams": "Microsoft Teams-Besprechung hinzufügen",
  "ev.videoLink": "Videolink",
  "ev.guestsPlaceholder": "Gäste per E-Mail hinzufügen",
  "ev.removeGuest": "{email} entfernen",
  "ev.invitesNote": "Gäste erhalten die Einladung von Ihrem Kalenderdienst.",
  "ev.availability": "Anzeigen als",
  "ev.busy": "Beschäftigt",
  "ev.freeOption": "Frei",
  "ev.reminder": "Erinnerung",
  "ev.reminder.default": "Standard des Kalenders",
  "ev.reminder.atStart": "Zu Beginn",
  "ev.reminder.minutes": "{n} Minuten vorher",
  "ev.reminder.hour": "1 Stunde vorher",
  "ev.reminder.2hours": "2 Stunden vorher",
  "ev.reminder.day": "1 Tag vorher",
  "ev.privateToggle": "Privat",
  "ev.privateHint": "Personen, mit denen Sie Ihren Kalender teilen, sehen nur, dass Sie beschäftigt sind.",
  "ev.description": "Beschreibung",
  "ev.scope": "Anwenden auf",
  "ev.scope.this": "Diesen Termin",
  "ev.scope.all": "Alle Termine der Serie",
  "ev.confirmDelete": "Diesen Termin löschen?",
  "ev.deleteNotifies": "Gäste werden über die Absage informiert.",
  "error.READ_ONLY": "Dieser Kalender ist schreibgeschützt.",
  "error.EVENT_REJECTED": "Der Kalenderdienst hat die Änderung abgelehnt.",
  "error.PROVIDER_CONFLICT": "Der Termin wurde anderswo geändert. Schließen Sie ihn und versuchen Sie es erneut.",
  "error.PROVIDER_NOT_CONFIGURED": "Diese Verbindung ist auf dieser Instanz noch nicht eingerichtet.",
  "error.PROVIDER_AUTH": "Der Server hat Benutzername oder Passwort abgelehnt.",
  "error.PROVIDER_UNREACHABLE": "Der Server ist nicht erreichbar. Prüfe die Adresse und versuche es erneut.",
  "error.PROVIDER_INVALID": "Unter dieser Adresse wurde kein nutzbarer Kalender gefunden. Prüfe sie und versuche es erneut.",
};

const es: Dict = {
  "lang.name": "Español",
  "common.language": "Idioma",
  "common.email": "Correo electrónico",
  "common.password": "Contraseña",
  "common.name": "Nombre",
  "common.save": "Guardar",
  "common.saved": "Guardado",
  "common.cancel": "Cancelar",
  "common.continue": "Continuar",
  "common.back": "Atrás",
  "common.loading": "Cargando…",
  "common.passwordHint": "Al menos 10 caracteres.",
  "common.backToLogin": "Volver a iniciar sesión",
  "login.title": "Iniciar sesión",
  "login.remember": "Mantener la sesión iniciada",
  "login.submit": "Iniciar sesión",
  "login.forgot": "¿Has olvidado tu contraseña?",
  "login.noAccount": "¿Aún no tienes cuenta?",
  "login.create": "Crear una cuenta",
  "signup.title": "Crea tu cuenta",
  "signup.invited": "Te han invitado con {email}.",
  "signup.submit": "Crear mi cuenta",
  "signup.haveAccount": "¿Ya tienes una cuenta?",
  "signup.closed": "Los registros están cerrados en esta instancia.",
  "signup.inviteOnly": "Esta instancia funciona solo con invitación. Pide un enlace de invitación a su administrador.",
  "signup.checkTitle": "Revisa tu bandeja de entrada",
  "signup.checkBody": "Hemos enviado un enlace de confirmación a {email}. Es válido durante 24 horas.",
  "signup.resend": "Reenviar el enlace",
  "signup.resent": "Si la dirección está pendiente de confirmación, te llegará un nuevo enlace.",
  "verify.title": "Confirmando tu dirección",
  "verify.working": "Un momento…",
  "forgot.title": "Restablece tu contraseña",
  "forgot.body": "Escribe tu correo. Si existe una cuenta, recibirás un enlace válido durante 1 hora.",
  "forgot.submit": "Enviar el enlace",
  "forgot.sent": "Si existe una cuenta con esta dirección, te llegará un enlace.",
  "reset.title": "Elige una nueva contraseña",
  "reset.submit": "Guardar la contraseña",
  "setup.title": "Instalar Maki Calendar",
  "setup.intro": "Tres pasos: tu cuenta de administrador, el envío de correos y quién puede registrarse.",
  "setup.step.admin": "Administrador",
  "setup.step.mail": "Correos",
  "setup.step.access": "Registros",
  "setup.brevoKey": "Clave API de Brevo",
  "setup.brevoHelp": "En Brevo, abre SMTP & API y luego Claves API. Autentica también tu dominio de envío (SPF y DKIM) para que los correos no acaben en spam.",
  "setup.fromEmail": "Dirección del remitente",
  "setup.fromName": "Nombre del remitente",
  "setup.timezone": "Zona horaria predeterminada",
  "setup.finish": "Terminar la instalación",
  "setup.noToken": "Este enlace de instalación está incompleto. Abre el enlace completo que muestra el script de instalación.",
  "setup.done": "La instalación ha terminado.",
  "mode.label": "¿Quién puede crear una cuenta?",
  "mode.invite": "Solo personas invitadas",
  "mode.invite.help": "Invitas a las personas por correo. Recomendado.",
  "mode.open": "Cualquiera",
  "mode.open.help": "Cualquiera que conozca la dirección puede registrarse.",
  "mode.closed": "Nadie",
  "mode.closed.help": "Ninguna cuenta nueva, ni siquiera con invitación.",
  "nav.calendar": "Calendario",
  "nav.settings": "Ajustes",
  "nav.logout": "Cerrar sesión",
  "home.hello": "Hola, {name}",
  "home.emptyTitle": "Conecta tu primer calendario",
  "home.emptyBody": "Tu cuenta está lista. La conexión de calendarios de Google, Microsoft 365, CalDAV e iCal llega en la próxima versión.",
  "home.soon": "Próxima versión",
  "settings.title": "Ajustes",
  "settings.account": "Mi cuenta",
  "settings.security": "Seguridad",
  "settings.instance": "Instancia",
  "settings.users": "Usuarios",
  "settings.adminOnly": "Administrador",
  "profile.timezone": "Zona horaria",
  "profile.weekStart": "Primer día de la semana",
  "profile.home": "Ciudad predeterminada",
  "profile.homePlaceholder": "Bruselas, por ejemplo",
  "profile.homeHint": "Las sugerencias de lugares se ordenan según su posición actual, o según esta ciudad si se desconoce.",
  "day.1": "Lunes",
  "day.0": "Domingo",
  "day.6": "Sábado",
  "password.current": "Contraseña actual",
  "password.new": "Nueva contraseña",
  "password.change": "Cambiar la contraseña",
  "password.changed": "Contraseña cambiada. Se han cerrado tus otras sesiones.",
  "instance.mail": "Correos (Brevo)",
  "instance.keySaved": "Hay una clave guardada. Déjalo vacío para conservarla.",
  "instance.test": "Enviar un correo de prueba",
  "instance.testSent": "Correo de prueba enviado a {email}.",
  "users.invite": "Invitar a alguien",
  "users.inviteSubmit": "Enviar la invitación",
  "users.invited": "Invitación enviada a {email}.",
  "users.disable": "Desactivar",
  "users.enable": "Activar",
  "users.delete": "Eliminar",
  "users.confirmDelete": "¿Eliminar la cuenta de {name}? No se puede deshacer.",
  "users.you": "tú",
  "users.admin": "administrador",
  "users.unverified": "correo sin confirmar",
  "users.disabled": "desactivado",
  "error.INVALID_CREDENTIALS": "Correo o contraseña incorrectos.",
  "error.EMAIL_NOT_VERIFIED": "Confirma primero tu dirección de correo con el enlace que recibiste.",
  "error.ACCOUNT_DISABLED": "El administrador ha desactivado esta cuenta.",
  "error.EMAIL_TAKEN": "Ya existe una cuenta con esta dirección.",
  "error.REGISTRATION_CLOSED": "Los registros están cerrados en esta instancia.",
  "error.INVITATION_REQUIRED": "Usa la dirección de correo que recibió la invitación.",
  "error.INVALID_TOKEN": "Este enlace ya no es válido. Pide uno nuevo.",
  "error.SETUP_DONE": "La instalación ya ha terminado. Inicia sesión.",
  "error.SETUP_TOKEN_INVALID": "El enlace de instalación es incorrecto. Usa el que muestra el script.",
  "error.SETUP_TOKEN_MISSING": "Falta SETUP_TOKEN en el archivo .env del servidor.",
  "error.MAIL_NOT_CONFIGURED": "El envío de correos aún no está configurado. El administrador puede hacerlo en Ajustes.",
  "error.MAIL_FAILED": "No se pudo enviar el correo. Revisa la clave de Brevo y la dirección del remitente.",
  "error.RATE_LIMITED": "Demasiados intentos. Espera un minuto y vuelve a intentarlo.",
  "error.VALIDATION": "Hay campos que corregir.",
  "error.NETWORK": "No se puede conectar con el servidor. Revisa tu conexión.",
  "error.UNAUTHENTICATED": "Tu sesión ha caducado. Vuelve a iniciar sesión.",
  "error.FORBIDDEN": "Solo el administrador puede hacer esto.",
  "error.CSRF": "Se ha rechazado la solicitud. Recarga la página y vuelve a intentarlo.",
  "error.INTERNAL": "Algo ha fallado en el servidor. Vuelve a intentarlo en un momento.",
  "error.field": "Revisa este campo.",
  "settings.calendars": "Calendarios",
  "cal.lead": "Añade tantas cuentas como quieras. Para cada calendario, elige su color, si se muestra, si bloquea tu página de reservas y qué ven por defecto las personas con las que compartes.",
  "cal.add": "Añadir una cuenta",
  "cal.google.sub": "Gmail o Google Workspace",
  "cal.microsoft.sub": "Outlook, trabajo o personal",
  "cal.caldav.sub": "Nextcloud, iCloud, Fastmail…",
  "cal.ics": "Enlace iCal",
  "cal.ics.sub": "Suscripción de solo lectura",
  "cal.notConfigured": "El administrador debe configurar primero la aplicación de {provider} en Ajustes, Instancia.",
  "cal.continueWith": "Continuar con {provider}",
  "cal.redirectNote": "Te enviaremos a {provider} para que permitas a Maki Calendar leer y editar tu calendario. No se pide acceso a tus correos.",
  "cal.service": "Servicio",
  "cal.serverUrl": "Dirección del servidor",
  "cal.username": "Usuario",
  "cal.appPassword": "Contraseña de aplicación",
  "cal.appPasswordHint": "Usa una contraseña de aplicación en lugar de tu contraseña principal. Es obligatoria para iCloud.",
  "cal.connect": "Conectar y buscar calendarios",
  "cal.icsUrl": "Enlace del calendario",
  "cal.icsName": "Nombre visible",
  "cal.subscribe": "Suscribirse",
  "cal.other": "Otro servidor",
  "cal.status.ok": "Sincronizado {when}",
  "cal.status.never": "Esperando la primera sincronización",
  "cal.status.syncing": "Sincronizando…",
  "cal.status.error": "Problema de sincronización: {error}",
  "cal.status.reauth": "Se ha denegado el acceso. Vuelve a conectar la cuenta.",
  "cal.sync": "Sincronizar ahora",
  "cal.reconnect": "Reconectar",
  "cal.remove": "Quitar",
  "cal.removeConfirm": "¿Quitar {label}? Sus calendarios desaparecen de Maki Calendar. No se borra nada en el proveedor.",
  "cal.removeOk": "Quitar la cuenta",
  "cal.keep": "Conservar",
  "cal.show": "Mostrar",
  "cal.blocks": "Bloquea reservas",
  "cal.shareDefault": "Compartido como",
  "cal.default": "Predeterminado",
  "cal.readOnly": "solo lectura",
  "cal.color": "Color de {name}",
  "cal.connected": "Cuenta de {provider} conectada. Sus calendarios se están sincronizando.",
  "cal.added": "Cuenta añadida. Sus calendarios se están sincronizando.",
  "cal.empty": "Todavía no hay ninguna cuenta conectada.",
  "vis.hidden": "Oculto",
  "vis.busy": "Ocupado",
  "vis.details": "Detalles",
  "oauth.not_configured": "Esta conexión aún no está configurada. El administrador debe configurar la aplicación en Ajustes, Instancia.",
  "oauth.cancelled": "Conexión cancelada.",
  "oauth.expired": "La conexión tardó demasiado. Inténtalo de nuevo.",
  "oauth.failed": "La conexión ha fallado. Inténtalo de nuevo.",
  "oauth.admin_consent": "Tu organización exige que un administrador apruebe Maki Calendar. Envía este enlace a tu administrador de Microsoft 365:",
  "instance.providers": "Aplicaciones de calendario (OAuth)",
  "instance.providersHelp": "Crea las aplicaciones en Google y Microsoft y pega aquí sus identificadores. Cada aplicación necesita la URI de redirección indicada abajo.",
  "instance.clientId": "Client ID",
  "instance.clientSecret": "Secreto de cliente",
  "instance.secretSaved": "Hay un secreto guardado. Déjalo vacío para conservarlo.",
  "instance.redirectUri": "URI de redirección",
  "home.upcoming": "Próximos 14 días",
  "home.nothing": "Nada previsto.",
  "home.manage": "Gestionar calendarios",
  "home.addFirst": "Añadir un calendario",
  "home.attention": "Una cuenta necesita tu atención.",
  "home.connectBody": "Conecta Google, Microsoft 365, CalDAV (Nextcloud, iCloud…) o un enlace iCal para verlo todo en un solo lugar.",
  "common.allDay": "Todo el día",
  "common.join": "Unirse",
  "common.today": "Hoy",
  "common.tomorrow": "Mañana",
  "view.week": "Semana",
  "view.month": "Mes",
  "view.agenda": "Agenda",
  "view.boxes": "Casillas",
  "view.label": "Vista",
  "cal.prev": "Anterior",
  "cal.next": "Siguiente",
  "cal.weekNumber": "Semana {n}",
  "cal.calendars": "Calendarios",
  "cal.toggleSidebar": "Calendarios y minicalendario",
  "cal.more": "+{n} más",
  "cal.earlier": "Mostrar días anteriores",
  "cal.later": "Mostrar días posteriores",
  "cal.windowEnd": "Los calendarios se sincronizan hasta 18 meses en adelante.",
  "cal.windowStart": "Los calendarios se sincronizan desde hace 6 meses.",
  "cal.noTitle": "(Sin título)",
  "cal.shortcuts": "Atajos: N nuevo evento, T hoy, J/K o ← → anterior y siguiente, D 3 W M L B para cambiar de vista, / buscar.",
  "ev.free": "Se muestra como libre",
  "ev.tentative": "Provisional",
  "ev.private": "Privado",
  "ev.recurring": "Se repite",
  "ev.declined": "Rechazado",
  "ev.guests": "Invitados",
  "ev.organizer": "Organizador",
  "ev.status.accepted": "Aceptado",
  "ev.status.declined": "Rechazado",
  "ev.status.tentative": "Quizás",
  "ev.status.needs-action": "Pendiente de respuesta",
  "ev.openMap": "Abrir en un mapa",
  "ev.close": "Cerrar",
  "ev.notes": "Notas",
  "ev.location": "Lugar",
  "cal.calendar": "Calendario",
  "guide.google": "Cómo crear la aplicación de Google",
  "guide.google.1": "Abra console.cloud.google.com y cree un proyecto (por ejemplo «Maki Calendar»).",
  "guide.google.2": "En API y servicios, Biblioteca, active «Google Calendar API».",
  "guide.google.3": "Abra Google Auth Platform (pantalla de consentimiento OAuth), Comenzar: nombre de la aplicación, correo de asistencia, público «Externo», correo de contacto.",
  "guide.google.4": "En Branding: página principal {home}, política de privacidad {privacy}, condiciones del servicio {terms}, dominio autorizado {domain}.",
  "guide.google.5": "En Acceso a los datos, añada los permisos openid, email, profile y https://www.googleapis.com/auth/calendar.",
  "guide.google.6": "En Clientes, Crear cliente, tipo «Aplicación web», URI de redirección autorizado: la dirección mostrada arriba.",
  "guide.google.7": "Pegue aquí el ID de cliente y el secreto, y Guarde.",
  "guide.google.8": "En Público, Publicar la aplicación. Para quitar el aviso «aplicación no verificada», solicite la verificación en el Centro de verificación: usa las páginas de arriba y un breve vídeo de demostración.",
  "guide.microsoft": "Cómo crear la aplicación de Microsoft",
  "guide.microsoft.1": "Inicie sesión en entra.microsoft.com con una cuenta que tenga su propio directorio (una cuenta gratuita de Azure crea uno).",
  "guide.microsoft.2": "Registros de aplicaciones, Nuevo registro. Cuentas admitidas: «Cuentas de cualquier directorio organizativo y cuentas personales de Microsoft». URI de redirección, tipo Web: la dirección mostrada arriba.",
  "guide.microsoft.3": "Copie el Id. de aplicación (cliente).",
  "guide.microsoft.4": "Certificados y secretos, Nuevo secreto de cliente (24 meses). Copie el Valor, no el Id. del secreto, y anote la fecha de caducidad.",
  "guide.microsoft.5": "Permisos de API, Agregar, Microsoft Graph, Delegados: openid, profile, email, offline_access, User.Read, Calendars.ReadWrite. No conceda el consentimiento de administrador.",
  "guide.microsoft.6": "Pegue aquí el Id. y el secreto, y Guarde.",
  "guide.microsoft.7": "Opcional: para evitar «se requiere aprobación del administrador» en algunas organizaciones, conviértase en editor verificado (Personalización de marca y propiedades) con un Id. de Microsoft Cloud Partner Program y un dominio verificado.",
  "legal.privacy": "Política de privacidad",
  "legal.terms": "Condiciones de uso",
  "instance.legal": "Páginas legales",
  "instance.legalHelp": "Google pide estas páginas para verificar su aplicación. Se generan a partir de lo que Maki Calendar hace realmente; puede sustituirlas.",
  "instance.legalOperator": "Responsable (persona o empresa)",
  "instance.legalContact": "Correo de contacto",
  "instance.legalEdit": "Editar el texto",
  "instance.legalGenerated": "Se usa el texto generado.",
  "instance.legalReset": "Volver al texto generado",
  "settings.status": "Estado",
  "status.version": "Versión",
  "status.since": "en marcha desde {when}",
  "status.database": "Base de datos",
  "status.mail": "Envío de correos",
  "status.push": "Sincronización inmediata",
  "status.pushOff": "Desactivada: la dirección debe ser pública y HTTPS.",
  "status.users": "Usuarios",
  "status.connections": "Cuentas conectadas",
  "status.failing": "Cuentas con problemas",
  "status.noFailing": "Ninguna cuenta tiene problemas.",
  "status.backups": "Copias de seguridad",
  "status.noBackups": "Aún no hay copias listadas. Se hacen cada noche a las 3:15.",
  "status.restore": "Restaurar: sudo ./scripts/restore.sh /var/backups/confluence/<archivo>",
  "status.errors": "Errores recientes",
  "status.noErrors": "No hay errores recientes.",
  "status.ok": "OK",
  "status.problem": "Problema",
  "bk.busyCalendars": "Estos calendarios bloquean su página de reservas: {list}. Cambie «Cuenta como ocupado» en Calendarios.",
  "bk.busyNone": "Ningún calendario cuenta como ocupado: se ofrecen todas las horas de su horario. Active «Cuenta como ocupado» en Calendarios.",
  "ev.scope.following": "Este evento y los siguientes",
  "ev.repeat.custom": "Esta serie usa una regla que el formulario no sabe mostrar (por ejemplo «el 2.º martes del mes»). Cámbiela en Google, Microsoft o su aplicación CalDAV.",
  "instance.places": "Búsqueda de lugares",
  "instance.placesHelp": "Por defecto, los lugares vienen de OpenStreetMap (gratis, sin clave). Con una clave API de Google Places, las sugerencias vienen de Google, que conoce más comercios y restaurantes. Google factura las búsquedas por encima de su cuota mensual gratuita.",
  "instance.placesKey": "Clave API de Google Places",
  "instance.placesSaved": "Hay una clave guardada. Déjelo vacío para conservarla.",
  "instance.placesNone": "Sin clave: se usa OpenStreetMap.",
  "instance.placesRemove": "Quitar la clave y volver a OpenStreetMap",
  "settings.notifications": "Notificaciones",
  "notify.intro": "Elija los correos que le envía Maki Calendar. Los visitantes siempre reciben la confirmación de su reserva.",
  "notify.bookings": "Reservas",
  "notify.bookings.hint": "Un correo por cada nueva reserva, cancelación o cambio en su página de reservas.",
  "notify.shares": "Nuevos calendarios compartidos",
  "notify.shares.hint": "Un correo cuando alguien comparte su calendario con usted.",
  "notify.reconnect": "Cuentas por volver a conectar",
  "notify.reconnect.hint": "Un correo cuando una cuenta lleva más de 24 horas sin sincronizarse.",
  "notify.dailyRecap": "Resumen del día siguiente",
  "notify.dailyRecap.hint": "Cada tarde a las 18 h, la lista de sus eventos del día siguiente.",
  "view.day": "Día",
  "view.threeDays": "3 días",
  "search.placeholder": "Buscar",
  "search.none": "No se ha encontrado ningún evento.",
  "cal.toggleStrip": "Mostrar más semanas",
  "settings.display": "Visualización",
  "display.startView": "Vista al abrir",
  "display.startAuto": "Semana (casillas en el teléfono)",
  "display.theme": "Tema",
  "display.theme.system": "Como el dispositivo",
  "display.theme.light": "Claro",
  "display.theme.dark": "Oscuro",
  "display.weekNumbers": "Mostrar los números de semana",
  "display.showBlocks": "Mostrar los bloques del bloqueo cruzado (atenuados) en lugar de ocultarlos",
  "profile.timeFormat": "Formato de hora",
  "profile.timeAuto": "Según el idioma",
  "data.title": "Mis datos",
  "data.exportBody": "Descargue todo lo que Maki Calendar guarda sobre usted, en un archivo JSON. No incluye contraseñas ni claves de acceso a las cuentas.",
  "data.export": "Descargar mis datos",
  "data.delete": "Eliminar mi cuenta",
  "data.deleteWarning": "Su cuenta, conexiones, calendarios compartidos, página de reservas y reservas se eliminan definitivamente. No se elimina nada en Google, Microsoft ni en su servidor CalDAV; se revoca el acceso de Maki Calendar.",
  "sessions.title": "Sesiones abiertas",
  "sessions.current": "Este dispositivo",
  "sessions.lastSeen": "activa {when}",
  "sessions.revoke": "Cerrar sesión",
  "sessions.revokeOthers": "Cerrar las demás sesiones",
  "totp.title": "Verificación en dos pasos",
  "totp.intro": "Tras la contraseña, Maki Calendar pide un código de una aplicación de autenticación (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).",
  "totp.setup": "Activar la verificación en dos pasos",
  "totp.step1": "Escanee este código QR con su aplicación de autenticación.",
  "totp.step2": "Introduzca el código de 6 cifras que muestra.",
  "totp.manual": "¿No puede escanear? Introduzca esta clave:",
  "totp.code": "Código",
  "totp.enable": "Activar",
  "totp.on": "La verificación en dos pasos está activa",
  "totp.codesLeft": "quedan {n} códigos de recuperación",
  "totp.newCodes": "Nuevos códigos de recuperación",
  "totp.disable": "Desactivar",
  "totp.saveCodes": "Guarde estos códigos de recuperación en un lugar seguro. Cada uno permite entrar una vez si pierde el teléfono. No se volverán a mostrar.",
  "totp.copyCodes": "Copiar los códigos",
  "totp.loginBody": "Introduzca el código de su aplicación de autenticación.",
  "totp.backupHint": "También puede introducir uno de sus códigos de recuperación.",
  "settings.blocking": "Bloqueo cruzado",
  "blc.intro": "Mantenga sus calendarios alineados: cada evento ocupado de los calendarios de origen crea un bloque privado «Ocupado» en el calendario de destino. Quien solo ve ese calendario sabe así que no está libre. Los bloques siguen a su origen cuando cambia o desaparece.",
  "blc.new": "Nueva regla",
  "blc.edit": "Editar la regla",
  "blc.none": "Aún no hay reglas.",
  "blc.count": "{n} bloques",
  "blc.lastRun": "actualizado {when}",
  "blc.sources": "Calendarios de origen",
  "blc.sourcesHint": "Sus eventos ocupados (ni libres ni rechazados) crean bloques.",
  "blc.pickSource": "Elija al menos un calendario de origen.",
  "blc.target": "Calendario de destino",
  "blc.targetHint": "Aquí se crean los bloques, privados y ocupados, sin invitados ni descripción.",
  "blc.title": "Título de los bloques",
  "blc.titleHint": "Ocupado, por ejemplo.",
  "blc.window": "Bloquear durante",
  "blc.allDay": "Incluir eventos de todo el día",
  "blc.active": "Regla activa",
  "blc.deleteConfirm": "¿Eliminar esta regla?",
  "blc.removeBlocks": "Eliminar también los {n} bloques ya creados",
  "share.resend": "Reenviar la invitación",
  "share.resent": "Invitación reenviada",
  "settings.booking": "Reservas",
  "bk.intro": "Permita que otros reserven un momento con usted. Solo se ofrecen horas libres en todos los calendarios que cuentan como ocupado.",
  "bk.page": "Página de reservas",
  "bk.address": "Dirección de la página",
  "bk.copyLink": "Copiar enlace",
  "bk.copied": "Copiado",
  "bk.slugFirst": "Elija primero la dirección de su página.",
  "bk.types": "Tipos de cita",
  "bk.newType": "Nuevo tipo de cita",
  "bk.minutes": "{n} min",
  "bk.inactive": "Desactivado",
  "bk.loc.video": "Videollamada",
  "bk.loc.place": "En persona",
  "bk.loc.phone": "Llamada telefónica",
  "bk.loc.none": "Sin lugar",
  "bk.name": "Nombre",
  "bk.typeAddress": "Dirección del enlace",
  "bk.slugUsed": "Otro tipo de cita usa esta dirección.",
  "bk.duration": "Duración",
  "bk.interval": "Horas de inicio",
  "bk.every": "cada {n} min",
  "bk.color": "Color",
  "bk.destination": "Añadir al calendario",
  "bk.schedule": "Horario",
  "bk.location": "Lugar",
  "bk.noVideo": "La videollamada solo se añade en calendarios de Google y Microsoft.",
  "bk.bufferBefore": "Margen antes",
  "bk.bufferAfter": "Margen después",
  "bk.notice": "Antelación mínima",
  "bk.horizon": "Reservable hasta",
  "bk.dailyLimit": "Límite por día",
  "bk.noLimit": "Sin límite",
  "bk.inMinutes": "minutos",
  "bk.inHours": "horas",
  "bk.inDays": "días de antelación",
  "bk.questions": "Preguntas",
  "bk.questionsHint": "Siempre se piden el nombre y el correo. Añada aquí sus propias preguntas.",
  "bk.questionLabel": "Pregunta",
  "bk.questionType": "Tipo de respuesta",
  "bk.q.text": "Texto corto",
  "bk.q.textarea": "Texto largo",
  "bk.q.choice": "Opción",
  "bk.q.checkbox": "Casilla",
  "bk.required": "Obligatoria",
  "bk.removeQuestion": "Quitar la pregunta",
  "bk.optionsHint": "Opciones, separadas por comas",
  "bk.addQuestion": "Añadir una pregunta",
  "bk.active": "Reservable",
  "bk.deleteConfirm": "¿Eliminar este tipo de cita? Las reservas existentes se quedan en su calendario.",
  "bk.schedules": "Horario",
  "bk.scheduleName": "Nombre",
  "bk.closed": "Cerrado",
  "bk.addRange": "Añadir horas",
  "bk.removeRange": "Quitar horas",
  "bk.overrides": "Excepciones por fecha",
  "bk.overridesHint": "Cierre un día o cambie su horario, por ejemplo en vacaciones.",
  "bk.overrideDate": "Fecha",
  "bk.addOverride": "Añadir una fecha",
  "bk.removeOverride": "Quitar la fecha",
  "bk.bookings": "Reservas",
  "bk.upcoming": "Próximas",
  "bk.past": "Pasadas",
  "bk.noBookings": "Aún no hay reservas.",
  "bk.cancelled": "Cancelada",
  "bk.cancelBooking": "Cancelar la reserva",
  "bk.cancelConfirm": "Se avisará a la persona.",
  "pub.bookWith": "Reservar un momento con",
  "pub.noTypes": "Ahora mismo no hay tipos de cita disponibles.",
  "pub.notFound": "Esta página no existe",
  "pub.notFoundBody": "Revise el enlace que ha recibido.",
  "pub.loc.video": "Videollamada (enlace por correo)",
  "pub.loc.phone": "Llamada telefónica",
  "pub.loc.none": "Detalles por correo",
  "pub.timezone": "Zona horaria",
  "pub.noSlots": "No hay horas libres este mes. Pruebe el siguiente.",
  "pub.otherTime": "Elegir otro momento",
  "pub.phone": "Número de teléfono",
  "pub.confirm": "Confirmar la reserva",
  "pub.confirmed": "Reserva hecha",
  "pub.confirmedBody": "Se ha enviado una confirmación a {email}, con la invitación de calendario.",
  "pub.manage": "Cambiar o cancelar",
  "pub.isCancelled": "Esta cita se ha cancelado.",
  "pub.reschedule": "Elegir otro momento",
  "pub.cancel": "Cancelar la cita",
  "pub.cancelConfirm": "¿Cancelar esta cita?",
  "pub.cancelledOk": "La cita está cancelada.",
  "pub.movedOk": "La cita ha cambiado de hora.",
  "pub.bookAgain": "Reservar de nuevo",
  "error.SLUG_TAKEN": "Esta dirección ya está en uso. Elija otra.",
  "error.SLOT_TAKEN": "Esta hora se acaba de reservar. Elija otra.",
  "settings.sharing": "Compartir",
  "share.busy": "Ocupado",
  "share.withMe": "Compartidos conmigo",
  "share.manage": "Gestionar lo compartido",
  "share.intro": "Comparta su calendario consolidado con otras personas. Para cada persona y cada calendario, elija lo que ve. Los cambios se aplican al instante.",
  "share.invite": "Compartir mi calendario",
  "share.sent": "Se ha invitado a {email}.",
  "share.send": "Compartir",
  "share.help.busy": "ve los horarios, marcados como «Ocupado». Nada más sale del servidor.",
  "share.help.details": "ve también el título, el lugar, la descripción y los invitados.",
  "share.help.hidden": "el calendario no aparece.",
  "share.mine": "Lo que comparto",
  "share.mineEmpty": "Aún no ha compartido su calendario.",
  "share.withMeEmpty": "Nadie ha compartido aún un calendario con usted.",
  "share.pending": "Pendiente de crear la cuenta",
  "share.active": "Activo",
  "share.visibility": "Visibilidad",
  "share.privateNote": "Los eventos marcados como privados en Google, Microsoft o en el servidor CalDAV siempre aparecen como ocupado. Un calendario añadido más tarde usa su visibilidad predeterminada.",
  "share.revokeConfirm": "¿Dejar de compartir con {name}? Pierde el acceso al instante.",
  "share.revoke": "Dejar de compartir",
  "share.label": "Nombre mostrado",
  "share.show": "Mostrar",
  "error.SHARE_EXISTS": "Su calendario ya está compartido con esta persona.",
  "ev.new": "Nuevo evento",
  "ev.edit": "Editar",
  "ev.delete": "Eliminar",
  "ev.create": "Crear",
  "ev.saving": "Guardando…",
  "ev.saved": "Guardado",
  "ev.deleted": "Evento eliminado",
  "ev.moveFailed": "No se pudo mover el evento.",
  "ev.cannotEdit": "Este calendario es de solo lectura: sus eventos no se pueden cambiar aquí.",
  "ev.titlePlaceholder": "Añadir un título",
  "ev.starts": "Empieza",
  "ev.ends": "Termina",
  "ev.endBeforeStart": "El final debe ser posterior al inicio.",
  "ev.conflict": "Se solapa con {list}.",
  "ev.repeat": "Repetir",
  "ev.repeat.none": "No se repite",
  "ev.repeat.daily": "Cada día",
  "ev.repeat.weekly": "Cada semana",
  "ev.repeat.monthly": "Cada mes",
  "ev.repeat.yearly": "Cada año",
  "ev.repeat.every": "Cada",
  "ev.repeat.unit.daily": "día(s)",
  "ev.repeat.unit.weekly": "semana(s)",
  "ev.repeat.unit.monthly": "mes(es)",
  "ev.repeat.unit.yearly": "año(s)",
  "ev.repeat.on": "El",
  "ev.repeat.ends": "Termina",
  "ev.repeat.never": "Nunca",
  "ev.repeat.onDate": "En una fecha",
  "ev.repeat.after": "Después de",
  "ev.repeat.times": "veces",
  "ev.repeat.locked": "Este evento se repite. Para cambiar su repetición, elija «Este evento y los siguientes» o «Todos los eventos de la serie» más abajo.",
  "ev.placePlaceholder": "Añadir un lugar o una dirección",
  "ev.placeAttribution": "Lugares © colaboradores de OpenStreetMap",
  "ev.meet": "Añadir videoconferencia de Google Meet",
  "ev.teams": "Añadir una reunión de Microsoft Teams",
  "ev.videoLink": "Enlace de vídeo",
  "ev.guestsPlaceholder": "Añadir invitados por correo",
  "ev.removeGuest": "Quitar {email}",
  "ev.invitesNote": "Los invitados reciben la invitación de su servicio de calendario.",
  "ev.availability": "Mostrar como",
  "ev.busy": "Ocupado",
  "ev.freeOption": "Libre",
  "ev.reminder": "Recordatorio",
  "ev.reminder.default": "Predeterminado del calendario",
  "ev.reminder.atStart": "Al inicio",
  "ev.reminder.minutes": "{n} minutos antes",
  "ev.reminder.hour": "1 hora antes",
  "ev.reminder.2hours": "2 horas antes",
  "ev.reminder.day": "1 día antes",
  "ev.privateToggle": "Privado",
  "ev.privateHint": "quienes ven su calendario compartido solo saben que está ocupado.",
  "ev.description": "Descripción",
  "ev.scope": "Aplicar a",
  "ev.scope.this": "Este evento",
  "ev.scope.all": "Todos los eventos de la serie",
  "ev.confirmDelete": "¿Eliminar este evento?",
  "ev.deleteNotifies": "Se avisará a los invitados de la cancelación.",
  "error.READ_ONLY": "Este calendario es de solo lectura.",
  "error.EVENT_REJECTED": "El servicio de calendario rechazó el cambio.",
  "error.PROVIDER_CONFLICT": "El evento cambió en otro lugar. Ciérrelo y vuelva a intentarlo.",
  "error.PROVIDER_NOT_CONFIGURED": "Esta conexión aún no está configurada en esta instancia.",
  "error.PROVIDER_AUTH": "El servidor rechazó el usuario o la contraseña.",
  "error.PROVIDER_UNREACHABLE": "No se puede conectar con el servidor. Revisa la dirección y vuelve a intentarlo.",
  "error.PROVIDER_INVALID": "No se encontró ningún calendario utilizable en esta dirección. Revísala y vuelve a intentarlo.",
};

const it: Dict = {
  "lang.name": "Italiano",
  "common.language": "Lingua",
  "common.email": "Email",
  "common.password": "Password",
  "common.name": "Nome",
  "common.save": "Salva",
  "common.saved": "Salvato",
  "common.cancel": "Annulla",
  "common.continue": "Continua",
  "common.back": "Indietro",
  "common.loading": "Caricamento…",
  "common.passwordHint": "Almeno 10 caratteri.",
  "common.backToLogin": "Torna all'accesso",
  "login.title": "Accedi",
  "login.remember": "Resta connesso",
  "login.submit": "Accedi",
  "login.forgot": "Password dimenticata?",
  "login.noAccount": "Non hai ancora un account?",
  "login.create": "Crea un account",
  "signup.title": "Crea il tuo account",
  "signup.invited": "Sei stato invitato con {email}.",
  "signup.submit": "Crea il mio account",
  "signup.haveAccount": "Hai già un account?",
  "signup.closed": "Le registrazioni sono chiuse su questa istanza.",
  "signup.inviteOnly": "Questa istanza funziona solo su invito. Chiedi un link di invito al suo amministratore.",
  "signup.checkTitle": "Controlla la tua casella di posta",
  "signup.checkBody": "Abbiamo inviato un link di conferma a {email}. È valido per 24 ore.",
  "signup.resend": "Invia di nuovo il link",
  "signup.resent": "Se l'indirizzo è in attesa di conferma, un nuovo link è in arrivo.",
  "verify.title": "Conferma del tuo indirizzo",
  "verify.working": "Un momento…",
  "forgot.title": "Reimposta la password",
  "forgot.body": "Inserisci la tua email. Se esiste un account, riceverai un link valido per 1 ora.",
  "forgot.submit": "Invia il link",
  "forgot.sent": "Se esiste un account per questo indirizzo, un link è in arrivo.",
  "reset.title": "Scegli una nuova password",
  "reset.submit": "Salva la password",
  "setup.title": "Installa Maki Calendar",
  "setup.intro": "Tre passaggi: il tuo account amministratore, l'invio delle email e chi può registrarsi.",
  "setup.step.admin": "Amministratore",
  "setup.step.mail": "Email",
  "setup.step.access": "Registrazioni",
  "setup.brevoKey": "Chiave API di Brevo",
  "setup.brevoHelp": "In Brevo apri SMTP & API, poi Chiavi API. Autentica anche il tuo dominio di invio (SPF e DKIM) perché le email non finiscano nello spam.",
  "setup.fromEmail": "Indirizzo del mittente",
  "setup.fromName": "Nome del mittente",
  "setup.timezone": "Fuso orario predefinito",
  "setup.finish": "Completa l'installazione",
  "setup.noToken": "Questo link di installazione è incompleto. Apri il link completo mostrato dallo script di installazione.",
  "setup.done": "L'installazione è completata.",
  "mode.label": "Chi può creare un account?",
  "mode.invite": "Solo le persone invitate",
  "mode.invite.help": "Inviti le persone via email. Consigliato.",
  "mode.open": "Chiunque",
  "mode.open.help": "Chiunque conosca l'indirizzo può registrarsi.",
  "mode.closed": "Nessuno",
  "mode.closed.help": "Nessun nuovo account, nemmeno con un invito.",
  "nav.calendar": "Calendario",
  "nav.settings": "Impostazioni",
  "nav.logout": "Esci",
  "home.hello": "Ciao, {name}",
  "home.emptyTitle": "Collega il tuo primo calendario",
  "home.emptyBody": "Il tuo account è pronto. Il collegamento dei calendari Google, Microsoft 365, CalDAV e iCal arriva nella prossima versione.",
  "home.soon": "Prossima versione",
  "settings.title": "Impostazioni",
  "settings.account": "Il mio account",
  "settings.security": "Sicurezza",
  "settings.instance": "Istanza",
  "settings.users": "Utenti",
  "settings.adminOnly": "Amministratore",
  "profile.timezone": "Fuso orario",
  "profile.weekStart": "Primo giorno della settimana",
  "profile.home": "Città predefinita",
  "profile.homePlaceholder": "Bruxelles, per esempio",
  "profile.homeHint": "I suggerimenti di luoghi sono ordinati intorno alla sua posizione attuale, o a questa città quando non è nota.",
  "day.1": "Lunedì",
  "day.0": "Domenica",
  "day.6": "Sabato",
  "password.current": "Password attuale",
  "password.new": "Nuova password",
  "password.change": "Cambia la password",
  "password.changed": "Password cambiata. Le tue altre sessioni sono state chiuse.",
  "instance.mail": "Email (Brevo)",
  "instance.keySaved": "C'è una chiave salvata. Lascia vuoto per mantenerla.",
  "instance.test": "Invia un'email di prova",
  "instance.testSent": "Email di prova inviata a {email}.",
  "users.invite": "Invita qualcuno",
  "users.inviteSubmit": "Invia l'invito",
  "users.invited": "Invito inviato a {email}.",
  "users.disable": "Disattiva",
  "users.enable": "Riattiva",
  "users.delete": "Elimina",
  "users.confirmDelete": "Eliminare l'account di {name}? Non si può annullare.",
  "users.you": "tu",
  "users.admin": "amministratore",
  "users.unverified": "email non confermata",
  "users.disabled": "disattivato",
  "error.INVALID_CREDENTIALS": "Email o password errate.",
  "error.EMAIL_NOT_VERIFIED": "Conferma prima il tuo indirizzo email con il link che hai ricevuto.",
  "error.ACCOUNT_DISABLED": "Questo account è stato disattivato dall'amministratore.",
  "error.EMAIL_TAKEN": "Esiste già un account con questo indirizzo.",
  "error.REGISTRATION_CLOSED": "Le registrazioni sono chiuse su questa istanza.",
  "error.INVITATION_REQUIRED": "Usa l'indirizzo email che ha ricevuto l'invito.",
  "error.INVALID_TOKEN": "Questo link non è più valido. Richiedine uno nuovo.",
  "error.SETUP_DONE": "L'installazione è già completata. Accedi.",
  "error.SETUP_TOKEN_INVALID": "Il link di installazione è errato. Usa quello mostrato dallo script.",
  "error.SETUP_TOKEN_MISSING": "SETUP_TOKEN manca nel file .env del server.",
  "error.MAIL_NOT_CONFIGURED": "L'invio delle email non è ancora configurato. L'amministratore può farlo nelle Impostazioni.",
  "error.MAIL_FAILED": "Impossibile inviare l'email. Controlla la chiave Brevo e l'indirizzo del mittente.",
  "error.RATE_LIMITED": "Troppi tentativi. Aspetta un minuto e riprova.",
  "error.VALIDATION": "Alcuni campi vanno corretti.",
  "error.NETWORK": "Impossibile raggiungere il server. Controlla la connessione.",
  "error.UNAUTHENTICATED": "La sessione è scaduta. Accedi di nuovo.",
  "error.FORBIDDEN": "Solo l'amministratore può farlo.",
  "error.CSRF": "La richiesta è stata rifiutata. Ricarica la pagina e riprova.",
  "error.INTERNAL": "Si è verificato un problema sul server. Riprova tra un attimo.",
  "error.field": "Controlla questo campo.",
  "settings.calendars": "Calendari",
  "cal.lead": "Aggiungi tutti gli account che vuoi. Per ogni calendario scegli il colore, se viene mostrato, se blocca la tua pagina di prenotazione e cosa vedono di default le persone con cui condividi.",
  "cal.add": "Aggiungi un account",
  "cal.google.sub": "Gmail o Google Workspace",
  "cal.microsoft.sub": "Outlook, lavoro o personale",
  "cal.caldav.sub": "Nextcloud, iCloud, Fastmail…",
  "cal.ics": "Link iCal",
  "cal.ics.sub": "Abbonamento in sola lettura",
  "cal.notConfigured": "L'amministratore deve prima configurare l'app {provider} in Impostazioni, Istanza.",
  "cal.continueWith": "Continua con {provider}",
  "cal.redirectNote": "Verrai indirizzato a {provider} per consentire a Maki Calendar di leggere e modificare il tuo calendario. Non viene richiesto l'accesso alle tue email.",
  "cal.service": "Servizio",
  "cal.serverUrl": "Indirizzo del server",
  "cal.username": "Nome utente",
  "cal.appPassword": "Password per app",
  "cal.appPasswordHint": "Usa una password per app invece della password principale. È obbligatoria per iCloud.",
  "cal.connect": "Collega e trova i calendari",
  "cal.icsUrl": "Link del calendario",
  "cal.icsName": "Nome visualizzato",
  "cal.subscribe": "Abbonati",
  "cal.other": "Altro server",
  "cal.status.ok": "Sincronizzato {when}",
  "cal.status.never": "In attesa della prima sincronizzazione",
  "cal.status.syncing": "Sincronizzazione…",
  "cal.status.error": "Problema di sincronizzazione: {error}",
  "cal.status.reauth": "L'accesso è stato negato. Ricollega l'account.",
  "cal.sync": "Sincronizza ora",
  "cal.reconnect": "Ricollega",
  "cal.remove": "Rimuovi",
  "cal.removeConfirm": "Rimuovere {label}? I suoi calendari spariscono da Maki Calendar. Presso il fornitore non viene eliminato nulla.",
  "cal.removeOk": "Rimuovi l'account",
  "cal.keep": "Mantieni",
  "cal.show": "Mostra",
  "cal.blocks": "Blocca prenotazioni",
  "cal.shareDefault": "Condiviso come",
  "cal.default": "Predefinito",
  "cal.readOnly": "sola lettura",
  "cal.color": "Colore di {name}",
  "cal.connected": "Account {provider} collegato. I calendari si stanno sincronizzando.",
  "cal.added": "Account aggiunto. I calendari si stanno sincronizzando.",
  "cal.empty": "Nessun account collegato per ora.",
  "vis.hidden": "Nascosto",
  "vis.busy": "Occupato",
  "vis.details": "Dettagli",
  "oauth.not_configured": "Questo collegamento non è ancora configurato. L'amministratore deve configurare l'app in Impostazioni, Istanza.",
  "oauth.cancelled": "Collegamento annullato.",
  "oauth.expired": "Il collegamento ha richiesto troppo tempo. Riprova.",
  "oauth.failed": "Il collegamento non è riuscito. Riprova.",
  "oauth.admin_consent": "La tua organizzazione richiede che un amministratore approvi Maki Calendar. Invia questo link al tuo amministratore Microsoft 365:",
  "instance.providers": "App di calendario (OAuth)",
  "instance.providersHelp": "Crea le app su Google e Microsoft, poi incolla qui i loro identificativi. Ogni app richiede l'URI di reindirizzamento indicato sotto.",
  "instance.clientId": "Client ID",
  "instance.clientSecret": "Segreto client",
  "instance.secretSaved": "C'è un segreto salvato. Lascia vuoto per mantenerlo.",
  "instance.redirectUri": "URI di reindirizzamento",
  "home.upcoming": "Prossimi 14 giorni",
  "home.nothing": "Niente in programma.",
  "home.manage": "Gestisci calendari",
  "home.addFirst": "Aggiungi un calendario",
  "home.attention": "Un account richiede la tua attenzione.",
  "home.connectBody": "Collega Google, Microsoft 365, CalDAV (Nextcloud, iCloud…) o un link iCal per vedere tutto in un unico posto.",
  "common.allDay": "Tutto il giorno",
  "common.join": "Partecipa",
  "common.today": "Oggi",
  "common.tomorrow": "Domani",
  "view.week": "Settimana",
  "view.month": "Mese",
  "view.agenda": "Agenda",
  "view.boxes": "Riquadri",
  "view.label": "Vista",
  "cal.prev": "Precedente",
  "cal.next": "Successivo",
  "cal.weekNumber": "Settimana {n}",
  "cal.calendars": "Calendari",
  "cal.toggleSidebar": "Calendari e minicalendario",
  "cal.more": "+{n} altri",
  "cal.earlier": "Mostra i giorni precedenti",
  "cal.later": "Mostra i giorni successivi",
  "cal.windowEnd": "I calendari sono sincronizzati fino a 18 mesi in avanti.",
  "cal.windowStart": "I calendari sono sincronizzati a partire da 6 mesi fa.",
  "cal.noTitle": "(Senza titolo)",
  "cal.shortcuts": "Scorciatoie: N nuovo evento, T oggi, J/K o ← → precedente e successivo, D 3 W M L B per cambiare vista, / cerca.",
  "ev.free": "Mostrato come libero",
  "ev.tentative": "Provvisorio",
  "ev.private": "Privato",
  "ev.recurring": "Si ripete",
  "ev.declined": "Rifiutato",
  "ev.guests": "Invitati",
  "ev.organizer": "Organizzatore",
  "ev.status.accepted": "Accettato",
  "ev.status.declined": "Rifiutato",
  "ev.status.tentative": "Forse",
  "ev.status.needs-action": "In attesa di risposta",
  "ev.openMap": "Apri in una mappa",
  "ev.close": "Chiudi",
  "ev.notes": "Note",
  "ev.location": "Luogo",
  "cal.calendar": "Calendario",
  "guide.google": "Come creare l'app Google",
  "guide.google.1": "Apra console.cloud.google.com e crei un progetto (per esempio «Maki Calendar»).",
  "guide.google.2": "In API e servizi, Libreria, attivi «Google Calendar API».",
  "guide.google.3": "Apra Google Auth Platform (schermata di consenso OAuth), Inizia: nome dell'app, e-mail di assistenza, pubblico «Esterno», e-mail di contatto.",
  "guide.google.4": "In Branding: home page {home}, norme sulla privacy {privacy}, termini di servizio {terms}, dominio autorizzato {domain}.",
  "guide.google.5": "In Accesso ai dati, aggiunga gli ambiti openid, email, profile e https://www.googleapis.com/auth/calendar.",
  "guide.google.6": "In Client, Crea client, tipo «Applicazione web», URI di reindirizzamento autorizzato: l'indirizzo mostrato sopra.",
  "guide.google.7": "Incolli qui l'ID client e il segreto, poi Salvi.",
  "guide.google.8": "In Pubblico, Pubblica l'app. Per togliere l'avviso «app non verificata», richieda la verifica nel Centro di verifica: usa le pagine qui sopra e un breve video dimostrativo.",
  "guide.microsoft": "Come creare l'app Microsoft",
  "guide.microsoft.1": "Acceda a entra.microsoft.com con un account che abbia una propria directory (un account Azure gratuito ne crea una).",
  "guide.microsoft.2": "Registrazioni app, Nuova registrazione. Account supportati: «Account in qualsiasi directory organizzativa e account Microsoft personali». URI di reindirizzamento, tipo Web: l'indirizzo mostrato sopra.",
  "guide.microsoft.3": "Copi l'ID applicazione (client).",
  "guide.microsoft.4": "Certificati e segreti, Nuovo segreto client (24 mesi). Copi il Valore, non l'ID segreto, e annoti la data di scadenza.",
  "guide.microsoft.5": "Autorizzazioni API, Aggiungi, Microsoft Graph, Delegate: openid, profile, email, offline_access, User.Read, Calendars.ReadWrite. Non conceda il consenso amministratore.",
  "guide.microsoft.6": "Incolli qui l'ID e il segreto, poi Salvi.",
  "guide.microsoft.7": "Facoltativo: per evitare «approvazione dell'amministratore necessaria» in alcune organizzazioni, diventi editore verificato (Personalizzazione e proprietà) con un ID Microsoft Cloud Partner Program e un dominio verificato.",
  "legal.privacy": "Informativa sulla privacy",
  "legal.terms": "Condizioni d'uso",
  "instance.legal": "Pagine legali",
  "instance.legalHelp": "Google chiede queste pagine per verificare l'app. Sono generate da ciò che Maki Calendar fa davvero; può sostituirle.",
  "instance.legalOperator": "Titolare (persona o società)",
  "instance.legalContact": "E-mail di contatto",
  "instance.legalEdit": "Modifica il testo",
  "instance.legalGenerated": "In uso il testo generato.",
  "instance.legalReset": "Torna al testo generato",
  "settings.status": "Stato",
  "status.version": "Versione",
  "status.since": "attivo da {when}",
  "status.database": "Database",
  "status.mail": "Invio e-mail",
  "status.push": "Sincronizzazione immediata",
  "status.pushOff": "Disattivata: l'indirizzo deve essere pubblico e HTTPS.",
  "status.users": "Utenti",
  "status.connections": "Account collegati",
  "status.failing": "Account con problemi",
  "status.noFailing": "Nessun account ha problemi.",
  "status.backups": "Backup",
  "status.noBackups": "Nessun backup elencato per ora. Avvengono ogni notte alle 3:15.",
  "status.restore": "Ripristino: sudo ./scripts/restore.sh /var/backups/confluence/<file>",
  "status.errors": "Errori recenti",
  "status.noErrors": "Nessun errore recente.",
  "status.ok": "OK",
  "status.problem": "Problema",
  "bk.busyCalendars": "Questi calendari bloccano la sua pagina di prenotazione: {list}. Cambi «Conta come occupato» in Calendari.",
  "bk.busyNone": "Nessun calendario conta come occupato: sono proposti tutti gli orari. Attivi «Conta come occupato» in Calendari.",
  "ev.scope.following": "Questo evento e i successivi",
  "ev.repeat.custom": "Questa serie usa una regola che il modulo non sa mostrare (per esempio «il 2° martedì del mese»). La modifichi in Google, Microsoft o nella sua app CalDAV.",
  "instance.places": "Ricerca di luoghi",
  "instance.placesHelp": "Per impostazione predefinita, i luoghi vengono da OpenStreetMap (gratuito, senza chiave). Con una chiave API Google Places, i suggerimenti vengono da Google, che conosce più negozi e ristoranti. Google addebita le ricerche oltre la quota mensile gratuita.",
  "instance.placesKey": "Chiave API Google Places",
  "instance.placesSaved": "È salvata una chiave. Lasci vuoto per mantenerla.",
  "instance.placesNone": "Nessuna chiave: si usa OpenStreetMap.",
  "instance.placesRemove": "Rimuovi la chiave e torna a OpenStreetMap",
  "settings.notifications": "Notifiche",
  "notify.intro": "Scelga le e-mail che Maki Calendar le invia. I visitatori ricevono sempre la conferma della prenotazione.",
  "notify.bookings": "Prenotazioni",
  "notify.bookings.hint": "Un'e-mail per ogni nuova prenotazione, annullamento o spostamento sulla sua pagina di prenotazione.",
  "notify.shares": "Nuove condivisioni",
  "notify.shares.hint": "Un'e-mail quando qualcuno condivide il suo calendario con lei.",
  "notify.reconnect": "Account da ricollegare",
  "notify.reconnect.hint": "Un'e-mail quando un account non si sincronizza da più di 24 ore.",
  "notify.dailyRecap": "Riepilogo del giorno dopo",
  "notify.dailyRecap.hint": "Ogni sera alle 18, l'elenco dei suoi eventi del giorno dopo.",
  "view.day": "Giorno",
  "view.threeDays": "3 giorni",
  "search.placeholder": "Cerca",
  "search.none": "Nessun evento trovato.",
  "cal.toggleStrip": "Mostra altre settimane",
  "settings.display": "Visualizzazione",
  "display.startView": "Vista all'avvio",
  "display.startAuto": "Settimana (riquadri sul telefono)",
  "display.theme": "Tema",
  "display.theme.system": "Come il dispositivo",
  "display.theme.light": "Chiaro",
  "display.theme.dark": "Scuro",
  "display.weekNumbers": "Mostra i numeri di settimana",
  "display.showBlocks": "Mostra i blocchi del blocco incrociato (sbiaditi) invece di nasconderli",
  "profile.timeFormat": "Formato dell'ora",
  "profile.timeAuto": "Come la lingua",
  "data.title": "I miei dati",
  "data.exportBody": "Scarichi tutto ciò che Maki Calendar conserva su di lei, in un file JSON. Password e chiavi di accesso agli account non sono incluse.",
  "data.export": "Scarica i miei dati",
  "data.delete": "Elimina il mio account",
  "data.deleteWarning": "Account, connessioni, condivisioni, pagina di prenotazione e prenotazioni vengono eliminati definitivamente. Nulla viene eliminato su Google, Microsoft o sul server CalDAV; l'accesso di Maki Calendar viene revocato.",
  "sessions.title": "Sessioni aperte",
  "sessions.current": "Questo dispositivo",
  "sessions.lastSeen": "attiva {when}",
  "sessions.revoke": "Disconnetti",
  "sessions.revokeOthers": "Disconnetti ovunque altrove",
  "totp.title": "Verifica in due passaggi",
  "totp.intro": "Dopo la password, Maki Calendar chiede un codice da un'app di autenticazione (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).",
  "totp.setup": "Attiva la verifica in due passaggi",
  "totp.step1": "Scansioni questo codice QR con la sua app di autenticazione.",
  "totp.step2": "Inserisca il codice a 6 cifre che mostra.",
  "totp.manual": "Non riesce a scansionare? Inserisca questa chiave:",
  "totp.code": "Codice",
  "totp.enable": "Attiva",
  "totp.on": "La verifica in due passaggi è attiva",
  "totp.codesLeft": "{n} codici di riserva rimasti",
  "totp.newCodes": "Nuovi codici di riserva",
  "totp.disable": "Disattiva",
  "totp.saveCodes": "Conservi questi codici di riserva in un luogo sicuro. Ognuno permette di accedere una volta se perde il telefono. Non verranno più mostrati.",
  "totp.copyCodes": "Copia i codici",
  "totp.loginBody": "Inserisca il codice della sua app di autenticazione.",
  "totp.backupHint": "Può anche inserire uno dei suoi codici di riserva.",
  "settings.blocking": "Blocco incrociato",
  "blc.intro": "Mantenga i calendari allineati: ogni evento occupato dei calendari di origine crea un blocco privato «Occupato» nel calendario di destinazione. Chi vede solo quel calendario sa così che non è libero. I blocchi seguono la loro origine quando si sposta o scompare.",
  "blc.new": "Nuova regola",
  "blc.edit": "Modifica la regola",
  "blc.none": "Ancora nessuna regola.",
  "blc.count": "{n} blocchi",
  "blc.lastRun": "aggiornato {when}",
  "blc.sources": "Calendari di origine",
  "blc.sourcesHint": "I loro eventi occupati (non liberi, non rifiutati) creano blocchi.",
  "blc.pickSource": "Scelga almeno un calendario di origine.",
  "blc.target": "Calendario di destinazione",
  "blc.targetHint": "Qui vengono creati i blocchi, privati e occupati, senza invitati né descrizione.",
  "blc.title": "Titolo dei blocchi",
  "blc.titleHint": "Occupato, per esempio.",
  "blc.window": "Bloccare per",
  "blc.allDay": "Includi gli eventi di tutto il giorno",
  "blc.active": "Regola attiva",
  "blc.deleteConfirm": "Eliminare questa regola?",
  "blc.removeBlocks": "Elimina anche i {n} blocchi già creati",
  "share.resend": "Invia di nuovo l'invito",
  "share.resent": "Invito inviato di nuovo",
  "settings.booking": "Prenotazioni",
  "bk.intro": "Permetta ad altri di prenotare un momento con lei. Sono proposti solo orari liberi in tutti i calendari che contano come occupato.",
  "bk.page": "Pagina di prenotazione",
  "bk.address": "Indirizzo della pagina",
  "bk.copyLink": "Copia link",
  "bk.copied": "Copiato",
  "bk.slugFirst": "Scelga prima l'indirizzo della sua pagina.",
  "bk.types": "Tipi di appuntamento",
  "bk.newType": "Nuovo tipo di appuntamento",
  "bk.minutes": "{n} min",
  "bk.inactive": "Disattivato",
  "bk.loc.video": "Videochiamata",
  "bk.loc.place": "Di persona",
  "bk.loc.phone": "Telefonata",
  "bk.loc.none": "Senza luogo",
  "bk.name": "Nome",
  "bk.typeAddress": "Indirizzo del link",
  "bk.slugUsed": "Un altro tipo di appuntamento usa questo indirizzo.",
  "bk.duration": "Durata",
  "bk.interval": "Orari di inizio",
  "bk.every": "ogni {n} min",
  "bk.color": "Colore",
  "bk.destination": "Aggiungi al calendario",
  "bk.schedule": "Orari",
  "bk.location": "Luogo",
  "bk.noVideo": "La videochiamata si aggiunge solo per i calendari Google e Microsoft.",
  "bk.bufferBefore": "Margine prima",
  "bk.bufferAfter": "Margine dopo",
  "bk.notice": "Preavviso minimo",
  "bk.horizon": "Prenotabile fino a",
  "bk.dailyLimit": "Limite al giorno",
  "bk.noLimit": "Nessun limite",
  "bk.inMinutes": "minuti",
  "bk.inHours": "ore",
  "bk.inDays": "giorni prima",
  "bk.questions": "Domande",
  "bk.questionsHint": "Nome ed e-mail sono sempre richiesti. Aggiunga qui le sue domande.",
  "bk.questionLabel": "Domanda",
  "bk.questionType": "Tipo di risposta",
  "bk.q.text": "Testo breve",
  "bk.q.textarea": "Testo lungo",
  "bk.q.choice": "Scelta",
  "bk.q.checkbox": "Casella",
  "bk.required": "Obbligatoria",
  "bk.removeQuestion": "Rimuovi domanda",
  "bk.optionsHint": "Scelte, separate da virgole",
  "bk.addQuestion": "Aggiungi una domanda",
  "bk.active": "Prenotabile",
  "bk.deleteConfirm": "Eliminare questo tipo di appuntamento? Le prenotazioni esistenti restano nel calendario.",
  "bk.schedules": "Orari",
  "bk.scheduleName": "Nome",
  "bk.closed": "Chiuso",
  "bk.addRange": "Aggiungi orari",
  "bk.removeRange": "Rimuovi orari",
  "bk.overrides": "Eccezioni per data",
  "bk.overridesHint": "Chiuda un giorno o ne cambi gli orari, per le ferie per esempio.",
  "bk.overrideDate": "Data",
  "bk.addOverride": "Aggiungi una data",
  "bk.removeOverride": "Rimuovi data",
  "bk.bookings": "Prenotazioni",
  "bk.upcoming": "In arrivo",
  "bk.past": "Passate",
  "bk.noBookings": "Ancora nessuna prenotazione.",
  "bk.cancelled": "Annullata",
  "bk.cancelBooking": "Annulla prenotazione",
  "bk.cancelConfirm": "La persona sarà avvisata.",
  "pub.bookWith": "Prenota un momento con",
  "pub.noTypes": "Al momento non ci sono tipi di appuntamento disponibili.",
  "pub.notFound": "Questa pagina non esiste",
  "pub.notFoundBody": "Controlli il link ricevuto.",
  "pub.loc.video": "Videochiamata (link via e-mail)",
  "pub.loc.phone": "Telefonata",
  "pub.loc.none": "Dettagli via e-mail",
  "pub.timezone": "Fuso orario",
  "pub.noSlots": "Nessun orario libero questo mese. Provi il prossimo.",
  "pub.otherTime": "Scegli un altro momento",
  "pub.phone": "Numero di telefono",
  "pub.confirm": "Conferma la prenotazione",
  "pub.confirmed": "Prenotato",
  "pub.confirmedBody": "Una conferma è stata inviata a {email}, con l'invito del calendario.",
  "pub.manage": "Modifica o annulla",
  "pub.isCancelled": "Questo appuntamento è stato annullato.",
  "pub.reschedule": "Scegli un altro momento",
  "pub.cancel": "Annulla l'appuntamento",
  "pub.cancelConfirm": "Annullare questo appuntamento?",
  "pub.cancelledOk": "L'appuntamento è annullato.",
  "pub.movedOk": "L'appuntamento è stato spostato.",
  "pub.bookAgain": "Prenota di nuovo",
  "error.SLUG_TAKEN": "Questo indirizzo è già in uso. Ne scelga un altro.",
  "error.SLOT_TAKEN": "Questo orario è appena stato preso. Ne scelga un altro.",
  "settings.sharing": "Condivisione",
  "share.busy": "Occupato",
  "share.withMe": "Condivisi con me",
  "share.manage": "Gestisci condivisioni",
  "share.intro": "Condivida il suo calendario consolidato con altre persone. Per ogni persona e ogni calendario, scelga cosa vede. Le modifiche valgono subito.",
  "share.invite": "Condividi il mio calendario",
  "share.sent": "Invito inviato a {email}.",
  "share.send": "Condividi",
  "share.help.busy": "vede gli orari, indicati come «Occupato». Nient'altro lascia il server.",
  "share.help.details": "vede anche titolo, luogo, descrizione e invitati.",
  "share.help.hidden": "il calendario non compare affatto.",
  "share.mine": "Le mie condivisioni",
  "share.mineEmpty": "Non ha ancora condiviso il suo calendario.",
  "share.withMeEmpty": "Nessuno ha ancora condiviso un calendario con lei.",
  "share.pending": "In attesa della creazione dell'account",
  "share.active": "Attivo",
  "share.visibility": "Visibilità",
  "share.privateNote": "Gli eventi contrassegnati come privati su Google, Microsoft o sul server CalDAV appaiono sempre come occupato. Un calendario aggiunto in seguito usa la sua visibilità predefinita.",
  "share.revokeConfirm": "Smettere di condividere con {name}? L'accesso viene tolto subito.",
  "share.revoke": "Smetti di condividere",
  "share.label": "Nome visualizzato",
  "share.show": "Mostra",
  "error.SHARE_EXISTS": "Il suo calendario è già condiviso con questa persona.",
  "ev.new": "Nuovo evento",
  "ev.edit": "Modifica",
  "ev.delete": "Elimina",
  "ev.create": "Crea",
  "ev.saving": "Salvataggio…",
  "ev.saved": "Salvato",
  "ev.deleted": "Evento eliminato",
  "ev.moveFailed": "Non è stato possibile spostare l'evento.",
  "ev.cannotEdit": "Questo calendario è di sola lettura: i suoi eventi non si possono modificare qui.",
  "ev.titlePlaceholder": "Aggiungi un titolo",
  "ev.starts": "Inizio",
  "ev.ends": "Fine",
  "ev.endBeforeStart": "La fine deve essere dopo l'inizio.",
  "ev.conflict": "Si sovrappone a {list}.",
  "ev.repeat": "Ripeti",
  "ev.repeat.none": "Non si ripete",
  "ev.repeat.daily": "Ogni giorno",
  "ev.repeat.weekly": "Ogni settimana",
  "ev.repeat.monthly": "Ogni mese",
  "ev.repeat.yearly": "Ogni anno",
  "ev.repeat.every": "Ogni",
  "ev.repeat.unit.daily": "giorno/i",
  "ev.repeat.unit.weekly": "settimana/e",
  "ev.repeat.unit.monthly": "mese/i",
  "ev.repeat.unit.yearly": "anno/i",
  "ev.repeat.on": "Il",
  "ev.repeat.ends": "Termina",
  "ev.repeat.never": "Mai",
  "ev.repeat.onDate": "In una data",
  "ev.repeat.after": "Dopo",
  "ev.repeat.times": "volte",
  "ev.repeat.locked": "Questo evento si ripete. Per cambiarne la ripetizione, scelga «Questo evento e i successivi» o «Tutti gli eventi della serie» qui sotto.",
  "ev.placePlaceholder": "Aggiungi un luogo o un indirizzo",
  "ev.placeAttribution": "Luoghi © contributori di OpenStreetMap",
  "ev.meet": "Aggiungi videoconferenza Google Meet",
  "ev.teams": "Aggiungi una riunione Microsoft Teams",
  "ev.videoLink": "Link video",
  "ev.guestsPlaceholder": "Aggiungi invitati via e-mail",
  "ev.removeGuest": "Rimuovi {email}",
  "ev.invitesNote": "Gli invitati ricevono l'invito dal suo servizio di calendario.",
  "ev.availability": "Mostra come",
  "ev.busy": "Occupato",
  "ev.freeOption": "Libero",
  "ev.reminder": "Promemoria",
  "ev.reminder.default": "Predefinito del calendario",
  "ev.reminder.atStart": "All'inizio",
  "ev.reminder.minutes": "{n} minuti prima",
  "ev.reminder.hour": "1 ora prima",
  "ev.reminder.2hours": "2 ore prima",
  "ev.reminder.day": "1 giorno prima",
  "ev.privateToggle": "Privato",
  "ev.privateHint": "chi vede il suo calendario condiviso sa solo che è occupato.",
  "ev.description": "Descrizione",
  "ev.scope": "Applica a",
  "ev.scope.this": "Questo evento",
  "ev.scope.all": "Tutti gli eventi della serie",
  "ev.confirmDelete": "Eliminare questo evento?",
  "ev.deleteNotifies": "Gli invitati saranno avvisati dell'annullamento.",
  "error.READ_ONLY": "Questo calendario è di sola lettura.",
  "error.EVENT_REJECTED": "Il servizio di calendario ha rifiutato la modifica.",
  "error.PROVIDER_CONFLICT": "L'evento è cambiato altrove. Lo chiuda e riprovi.",
  "error.PROVIDER_NOT_CONFIGURED": "Questo collegamento non è ancora configurato su questa istanza.",
  "error.PROVIDER_AUTH": "Il server ha rifiutato nome utente o password.",
  "error.PROVIDER_UNREACHABLE": "Il server non è raggiungibile. Controlla l'indirizzo e riprova.",
  "error.PROVIDER_INVALID": "Nessun calendario utilizzabile trovato a questo indirizzo. Controllalo e riprova.",
};

export const MESSAGES: Record<Locale, Dict> = { en, fr, nl, de, es, it };
